/**
 * OrderPage - Clean Shopify-inspired checkout
 * Route: /order/:sessionId
 */

import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import Fuse from "fuse.js/basic";

import {
  useCart,
  useMarketplaceSession,
  useCustomerLookup,
  useSession,
  useOrdersBySession,
  useValidateDiscount,
  useCreateDraftOrder,
  usePrefilOrder,
} from "@/api/hooks";
import {
  formatCurrency,
  validateSAPhone,
  validateSAPostalCode,
  SA_PROVINCES,
  cn,
  formatPhoneToE164,
  clearDiscount as clearDiscountStorage,
  areConditionsMet,
  getSessionToken,
} from "@/lib/utils";
import { useDiscountStore, type DiscountDetail } from "@/lib/discount-store";
import { useOrderDetailsStore } from "@/lib/order-details-store";
import { readMagentoCartIds, returnToExpressCart, saveSessionCartToken } from "@/lib/express-cart/device";
import { useBrandTheme } from "@/lib/brand-theme";
import { BrandLogo } from "@/components/BrandLogo";
import { useTheme } from "@/lib/theme/ThemeProvider";
import { trackCheckoutError } from "@/lib/analytics";
import {
  fetchRetailerConfig,
  fetchIntegrationConfig,
  type ShippingMethod,
  type MarketplaceRetailer,
  fetchBobgoShipping,
  fetchPickupLocations,
  createCumiCustomer,
  searchCumiCustomer,
  type CumiFareyeLocation,
} from "@/lib/supabase-api";
import {
  fetchMagentoShipping,
} from "@/lib/magento-helper";
import {
  fetchStoresForAmastyShipping,
  getCityCoords,
  detectUserLocation,
  type AmastyStoreLocation,
  type GeocodingResult,
} from "@/lib/amasty-api";

import { Button } from "@/components/Button";
import { Card } from "@/components/Card";
import { Input } from "@/components/Input";
import { Select } from "@/components/Select";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { ErrorMessage } from "@/components/ErrorMessage";
import { CartItem } from "@/components/CartItem";
import { Checkbox } from "@/components/Checkbox";
import { CheckoutBreadcrumb } from "@/components/CheckoutBreadcrumb";
import { CumiSuburbLookup } from "@/components/CumiSuburbLookup";
import { SecureCheckoutBadge } from "@/components/SecureCheckoutBadge";
import { AutomatedDiscountRow } from "@/components/AutomatedDiscountRow";
import type { ShippingInfo, MarketplaceOrderShipping, CustomerGets, CartItem as CartItemType, BrandShippingQuote } from "@/types";

// Forward GeocodingResult to satisfy isolatedModules exports
export type { GeocodingResult };

// ---------------------------------------------------------------------------
//#region Types & Constants
// ---------------------------------------------------------------------------

type Step = "shipping" | "review";

interface PickupLocation {
  id: string;
  name: string;
  full_name: string;
  address: string;
  distance?: number;
  working_time_today?: string;
}

interface ShippingFormData extends ShippingInfo {
  shipping_option_id: string;
}

interface BrandShippingState {
  selectedOptionId: string;
  availableMethods: ShippingMethod[];
  loading: boolean;
  error: string | null;
  pickupStore: string | null;
  pickupStores: AmastyStoreLocation[];
  loadingPickupStores: boolean;
  pickupError: string | null;
  pickupCity: string;
  pickupCityCandidates: GeocodingResult[];
  showPickupCitySelection: boolean;
}

const initialFormData: ShippingFormData = {
  first_name: "",
  last_name: "",
  phone: "",
  email: "",
  address: "",
  address2: "",
  suburb: "",
  city: "",
  postal_code: "",
  province: "",
  shipping_option_id: "",
};

const initialBrandShippingState: BrandShippingState = {
  selectedOptionId: "",
  availableMethods: [],
  loading: false,
  error: null,
  pickupStore: null,
  pickupStores: [],
  loadingPickupStores: false,
  pickupError: null,
  pickupCity: "",
  pickupCityCandidates: [],
  showPickupCitySelection: false,
};

// ---------------------------------------------------------------------------
//#endregion
// ---------------------------------------------------------------------------

export function OrderPage() {
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();
  const { theme } = useBrandTheme();
  const { resolvedTheme } = useTheme();
  const queryClient = useQueryClient();

  // -------------------------------------------------------------------------
  //#region A. State declarations (grouped by concern)
  // -------------------------------------------------------------------------

  // Checkout step
  const [step, setStep] = useState<Step>("shipping");

  // Shipping form
  const [formData, setFormData] = useState<ShippingFormData>(initialFormData);
  const [formErrors, setFormErrors] = useState<
    Partial<Record<keyof ShippingFormData, string>>
  >({});
  const [isFormPrefilled, setIsFormPrefilled] = useState(false);
  const [updateContactDetails, setUpdateContactDetails] = useState(false);
  const [updateShippingDetails, setUpdateShippingDetails] = useState(false);
  const [cumiCustomerId, setCumiCustomerId] = useState<
    string | null | undefined
  >(undefined);
  const [selectedCumiFareyeCode, setSelectedCumiFareyeCode] = useState("");

  // Shipping methods (single-brand)
  const [shippingData, setShippingData] = useState<ShippingMethod[]>([]);
  const [apiConfig, setApiConfig] = useState<{
    shipping_config: unknown;
    base_url: string;
    type: string;
    bobgo: boolean;
  } | null>(null);
  const [enforceShipping, setEnforceShipping] = useState(false);
  const [calculatingShipping, setCalculatingShipping] = useState(false);

  // Store pickup
  const [pickupLocations, setPickupLocations] = useState<PickupLocation[]>([]);
  const [selectedPickupLocationId, setSelectedPickupLocationId] = useState<
    string | null
  >(null);
  const [selectedPickupLocationNames, setSelectedPickupLocationNames] = useState<{name: string, full_name: string}>({name: '', full_name:''});
  const [loadingPickupLocations, setLoadingPickupLocations] = useState(false);
  const [pickupLocationsError, setPickupLocationsError] = useState<string | null>(
    null,
  );
  const [pickupLocationSearch, setPickupLocationSearch] = useState("");
  const [pickupCity, setPickupCity] = useState("");
  const [cityCandidates, setCityCandidates] = useState<GeocodingResult[]>([]);
  const [showCitySelection, setShowCitySelection] = useState(false);

  // Discount
  const [discountCode, setDiscountCode] = useState("");
  const [discountError, setDiscountError] = useState<string | null>(null);
  const [discountWarning, setDiscountWarning] = useState<string | null>(null);
  const [discountPercentage, setDiscountPercentage] = useState<number | null>(
    null,
  );
  const [discountAppliedAt, setDiscountAppliedAt] = useState<number>(0); // bump to trigger recalc
  const [discountRecalculating, setDiscountRecalculating] = useState(false); // stays true until recalc useEffect finishes
  const [totalDiscountsSetCents, setTotalDiscountsSetCents] = useState<number>(0); // from API totalDiscountsSet, used to detect auto discounts
  const validDiscountCodesRef = useRef<string[]>([]); // from API newDiscounts, used to filter stale discounts during recalc
  const { totalDiscountDetail, addDiscount, removeDiscount, removeDiscountsByCode, updateDiscount, clearAll: clearDiscountStore } = useDiscountStore();
  const setOrderDetails = useOrderDetailsStore((s) => s.setOrderDetails);
  const storedAutomatedDiscountCents = useOrderDetailsStore((s) => s.automatedDiscountCents);

  // Marketplace shipping per fulfillment brand
  const [isMarketplace, setIsMarketplace] = useState(false);
  const [marketplaceRetailers, setMarketplaceRetailers] = useState<MarketplaceRetailer[]>([]);
  const [brandShippingMap, setBrandShippingMap] = useState<Map<string, BrandShippingState>>(new Map());

  // Order creation error shown to customer
  const [orderError, setOrderError] = useState<string | null>(null);
  const [isPrefilPending, setIsPrefilPending] = useState(false);

  // Mobile summary toggle
  const [showSummary, setShowSummary] = useState(false);
  const mobileBottomBarRef = useRef<HTMLDivElement>(null);
  const [mobileBarHeight, setMobileBarHeight] = useState(200);

  // Flags / refs
  const [hasEmptyCart, setHasEmptyCart] = useState(false);
  const [hasCheckedReturningCustomer, setHasCheckedReturningCustomer] =
    useState(false);
  const hasCheckedOrder = useRef(false);
  const cumiMetadataSyncedSessionRef = useRef<string | null>(null);

  // -------------------------------------------------------------------------
  //#endregion
  // -------------------------------------------------------------------------

  // -------------------------------------------------------------------------
  //#region B. Data fetching hooks
  // -------------------------------------------------------------------------

  const {
    data: cart,
    isLoading: cartLoading,
    error: cartError,
  } = useCart(sessionId || null);
  const magentoCartIds = readMagentoCartIds(sessionId);
  const magentoCartId = theme?.id ? magentoCartIds[theme.id] ?? null : null;
  const { data: sessionData } = useSession(sessionId || null);
  const { data: orders } = useOrdersBySession(hasEmptyCart && !cartLoading);
  const {
    data: marketplaceSession,
    isLoading: marketplaceSessionLoading,
  } = useMarketplaceSession(isMarketplace ? sessionId || null : null);

  const customerLookup = useCustomerLookup();
  const validateDiscount = useValidateDiscount();
  const createDraftOrder = useCreateDraftOrder();
  const prefilOrderMutation = usePrefilOrder();

  // Ref to access current discount store without adding to useEffect deps
  const totalDiscountDetailRef = useRef(totalDiscountDetail);
  totalDiscountDetailRef.current = totalDiscountDetail;

  // Refs for marketplace state used in the recalc useEffect without adding deps
  const isMarketplaceRef = useRef(isMarketplace);
  isMarketplaceRef.current = isMarketplace;
  const brandShippingMapRef = useRef(brandShippingMap);
  brandShippingMapRef.current = brandShippingMap;

  // -------------------------------------------------------------------------
  //#endregion
  // -------------------------------------------------------------------------

  // -------------------------------------------------------------------------
  //#region C. Derived values (useMemo)
  // -------------------------------------------------------------------------

  const hasCumiIntegration = useMemo(
    () =>
      apiConfig?.type?.toLowerCase() === "cumi" ||
      marketplaceRetailers.some(
        (retailer) => retailer.integration_type?.toLowerCase() === "cumi",
      ),
    [apiConfig?.type, marketplaceRetailers],
  );

  // Group cart items by fulfillment brand for marketplace mode
  const cartItemsByBrand = useMemo(() => {
    if (!cart) return new Map<string, CartItemType[]>();
    const groups = new Map<string, CartItemType[]>();
    for (const item of cart.items) {
      const brandId = item.fulfillment_brand_id || "default";
      const existing = groups.get(brandId) || [];
      existing.push(item);
      groups.set(brandId, existing);
    }
    return groups;
  }, [cart]);

  const brandCount = cartItemsByBrand.size;

  // Get available shipping methods based on conditions (non-marketplace / single-brand)
  const availableShippingMethods = useMemo(() => {
    if (!Array.isArray(shippingData)) return [];
    return shippingData.filter((option) => {
      if (option.countries && !option.countries.includes("South Africa")) {
        return false;
      }
      const provinceMatch =
        !option.provinces ||
        option.provinces.length === 0 ||
        option.provinces.some(
          (p) => p.toLowerCase() === formData.province.toLowerCase(),
        );
      const conditionsMet = areConditionsMet(
        option,
        cart?.subtotal_cents || 0,
      );
      return provinceMatch && conditionsMet;
    });
  }, [shippingData, formData.province, cart?.subtotal_cents]);

  const selectedShippingOption = useMemo(
    () =>
      availableShippingMethods.find(
        (option) =>
          option.id === formData.shipping_option_id ||
          option.method_code === formData.shipping_option_id ||
          `shipping-${availableShippingMethods.indexOf(option)}` ===
            formData.shipping_option_id,
      ),
    [availableShippingMethods, formData.shipping_option_id],
  );
  const isStorePickupOption =
    selectedShippingOption?.type?.toLowerCase() === "store";
  const pickupLocationFuse = useMemo(
    () =>
      new Fuse(pickupLocations, {
        keys: [
          { name: "name", weight: 0.6 },
          { name: "address", weight: 0.4 },
        ],
        threshold: 0.35,
        ignoreLocation: true,
      }),
    [pickupLocations],
  );
  const visiblePickupLocations = useMemo(
    () => {
      const query = pickupLocationSearch.trim();
      if (!isStorePickupOption || !query) return pickupLocations;

      return pickupLocationFuse.search(query).map(({ item }: any) => item);
    },
    [
      isStorePickupOption,
      pickupLocationFuse,
      pickupLocationSearch,
      pickupLocations,
    ],
  );

  const singleShippingCents = selectedShippingOption?.price_cents || 0;
  const marketplaceShippingCents = useMemo(() => {
    if (!isMarketplace) return 0;
    let total = 0;
    for (const [, brandState] of brandShippingMap) {
      const selected = brandState.availableMethods.find(
        (option) =>
          option.id === brandState.selectedOptionId ||
          option.method_code === brandState.selectedOptionId ||
          `shipping-${brandState.availableMethods.indexOf(option)}` ===
            brandState.selectedOptionId,
      );
      total += selected?.price_cents || 0;
    }
    return total;
  }, [brandShippingMap, isMarketplace]);

  const shippingCents = isMarketplace ? marketplaceShippingCents : singleShippingCents;
  // VAT (15%) applies to the items subtotal only - shipping and discounts are excluded
  const vatCents = Math.round((cart?.subtotal_cents ?? 0) * 0.15);
  const totalCents = (cart?.subtotal_cents ?? 0) + shippingCents + vatCents;

  // Tally up all applied discounts for the total discount amount
  // Tally up all applied discounts for the total discount amount
  const totalAppliedDiscount = totalDiscountDetail.reduce(
    (sum, d) => sum + d.totalValue,
    0,
  );

  const automatedDiscountCents = totalDiscountsSetCents > 0
    ? Math.max(0, totalDiscountsSetCents - totalAppliedDiscount)
    : 0;

  // Helper: get discount info for a cart item from the store
  function getDiscountForCartItem(item: { id?: string; product_id: string; sku?: string | null; quantity: number; line_total_cents: number; unit_price_cents: number }): boolean | number {
    for (const d of totalDiscountDetail) {
      if (d.isShippingDiscount) continue;
      if (!d.discountsOn.includes(item.id ?? "")) continue;

      // Check ineligible items
      if (d.ineligibleItems?.some((inc) => inc.cart_id === item.id || inc.product_id === item.product_id)) continue;

      if (d.type === "DiscountCodeBasic" && d.customerGets && d.customerGets.length > 0) {
        const matchedCg = d.customerGets.find((cg) => {
          if (cg.product_id !== item.product_id) return false;
          if (cg.selected_variant != null && cg.selected_variant !== item.sku) return false;
          return true;
        });
        if (!matchedCg) continue;
        const isFlatRate = d.discountValue > 1;
        const discountAmount = isFlatRate
          ? item.quantity * (d.discountValue * 100)
          : item.line_total_cents * d.discountValue;
        const discountedTotal = item.line_total_cents - discountAmount;
        return discountedTotal <= 0 ? true : discountedTotal;
      }

      if (d.type === "DiscountCodeBxgy") {
        const sameProductCg = d.customerGets?.find(
          (cg) => "discountedQTY" in cg && cg.product_id === item.product_id,
        );
        if (sameProductCg && "discountedQTY" in sameProductCg) {
          const unitPrice = item.quantity > 0 ? item.line_total_cents / item.quantity : item.unit_price_cents;
          const discountAmount =
            d.discountValue > 1
              ? Math.round(d.discountValue * 100 * sameProductCg.discountedQTY)
              : Math.round(sameProductCg.discountedQTY * unitPrice * d.discountValue);
          const discountedTotal = item.line_total_cents - discountAmount;
          return discountedTotal <= 0 ? true : discountedTotal;
        }

        const diffCg = d.customerGets?.find(
          (cg) => "quantity" in cg && cg.product_id === item.product_id,
        );
        if (diffCg) {
          if (d.discountValue > 1) {
            const qty = "quantity" in diffCg ? diffCg.quantity : 1;
            const discountedTotal = item.line_total_cents - Math.round(d.discountValue * 100 * qty);
            return discountedTotal <= 0 ? true : discountedTotal;
          }
          if (d.discountValue === 1) return true;
          const discountedTotal = Math.round(item.line_total_cents * (1 - d.discountValue));
          return discountedTotal <= 0 ? true : discountedTotal;
        }
      }

      // Order-level discount (DiscountCodeBasic without customerGets, Magento)
      if (d.type === "DiscountCodeBasic" || d.type === "Magento") {
        if (d.codeType === "DiscountPercentage") {
          const discountAmount = Math.round(item.line_total_cents * d.discountValue);
          const discountedTotal = item.line_total_cents - discountAmount;
          return discountedTotal <= 0 ? true : discountedTotal;
        } else {
          // Flat rate - proportionally distributed per item
          const discountAmount = d.discountValue > 1
            ? Math.round(d.discountValue * 100 * item.quantity)
            : Math.round(d.discountValue * item.quantity);
          const discountedTotal = item.line_total_cents - discountAmount;
          return discountedTotal <= 0 ? true : discountedTotal;
        }
      }
    }
    return false;
  }

  // -------------------------------------------------------------------------
  //#endregion
  // -------------------------------------------------------------------------

  // -------------------------------------------------------------------------
  //#region D. Event handlers
  // -------------------------------------------------------------------------

  // Always return to the standalone cart, which owns cart edits and checks.
  const handleBackToCart = () => {
    if (sessionId) {
      const token = getSessionToken();
      if (token) saveSessionCartToken(sessionId, token);
      if (!returnToExpressCart(sessionId)) {
        navigate(`/cart?session=${encodeURIComponent(sessionId)}`);
      }
      return;
    }
  };

  const handleProceedToReview = async () => {
    if (!validateForm()) return;
    if (enforceShipping && !formData.shipping_option_id && !isMarketplace) {
      setFormErrors((prev) => ({
        ...prev,
        shipping_option_id: "Please select a shipping method to continue",
      }));
      return;
    }
    if (isMarketplace) {
      const missing = getMarketplaceMissingShippingBrands();
      if (missing.length > 0) {
        for (const brandId of missing) {
          updateBrandShipping(brandId, { error: "Select a shipping method" });
        }
        return;
      }
    }
    setStep("review");
    window.scrollTo(0, 0);
  };

  const handleBackToShipping = () => {
    setStep("shipping");
    window.scrollTo(0, 0);
  };

  const handleInputChange = (field: keyof ShippingFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    if (field === "phone") {
      setHasCheckedReturningCustomer(false);
      if (!validateSAPhone(value) && value.trim() !== "") {
        setFormErrors((prev) => ({
          ...prev,
          phone: "Enter a valid South African phone number",
        }));
      } else if (value.trim() !== "") {
        const cleaned = formatPhoneToE164(value);
        setFormData((prev) => ({ ...prev, phone: cleaned }));
      }
    }
  };

  const handlePhoneBlur = async () => {
    const phone = formData.phone.trim();
    if (hasCheckedReturningCustomer) return;
    if (!validateSAPhone(phone)) return;
    if (!theme?.id) return;

    setHasCheckedReturningCustomer(true);

    try {
      const result = await customerLookup.mutateAsync({
        phoneNumber: phone,
        retailerId: theme.id,
      });

      if (!result.found || !result.shipping) return;

      setFormData((prev) => ({
        ...prev,
        name: result.shipping?.name || "",
        email: result.shipping?.email || "",
        address: result.shipping?.address || "",
        city: hasCumiIntegration ? "" : result.shipping?.city || "",
        postal_code: hasCumiIntegration
          ? ""
          : result.shipping?.postal_code || "",
        province: hasCumiIntegration ? "" : result.shipping?.province || "",
      }));
    } catch (error) {
      if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
        Sentry.captureException(error, {
          tags: { component: "OrderPage", action: "customerLookup", phone },
        });
      }
    }
  };

  const handleCumiSuburbChange = (suburb: string) => {
    setSelectedCumiFareyeCode("");
    setFormData((prev) => ({
      ...prev,
      suburb,
      city: "",
      postal_code: "",
      province: "",
    }));
    setFormErrors((prev) => ({
      ...prev,
      suburb: undefined,
      city: undefined,
      postal_code: undefined,
      province: undefined,
    }));
  };

  const handleCumiLocationSelect = (
    location: CumiFareyeLocation | null,
  ) => {
    setSelectedCumiFareyeCode(location?.code ?? "");
    if (!location) {
      setFormData((prev) => ({
        ...prev,
        city: "",
        postal_code: "",
        province: "",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      suburb: location.suburb,
      city: location.city,
      postal_code: location.postal_code,
      province: location.province,
    }));
    setFormErrors((prev) => ({
      ...prev,
      suburb: undefined,
      city: undefined,
      postal_code: undefined,
      province: undefined,
    }));
  };

  const handleShippingOptionChange = (option: {
    id: string;
    name: string;
    description: string;
    price_cents: number;
    type?: string;
    carrier_code?: string;
    method_code?: string;
  }) => {
    setFormData((prev) => ({ ...prev, shipping_option_id: option.id }));
    if (formErrors.shipping_option_id) {
      setFormErrors((prev) => ({ ...prev, shipping_option_id: undefined }));
    }
    localStorage.setItem("selectedShippingMethod", JSON.stringify(option));

    setPickupLocations([]);
    setSelectedPickupLocationId(null);
    setPickupLocationsError(null);
    setLoadingPickupLocations(false);
    setPickupLocationSearch("");
    setCityCandidates([]);
    setShowCitySelection(false);
  };

  const handleBrandShippingOptionChange = (
    brandId: string,
    option: {
      id: string;
      name: string;
      description: string;
      price_cents: number;
      carrier_code?: string;
      method_code?: string;
    },
  ) => {
    const retailer = marketplaceRetailers.find((r) => r.retailer_id === brandId);
    const baseUrl = retailer?.base_url ?? apiConfig?.base_url;
    const isAmastyOption =
      option.name?.toLowerCase().includes("amstorepickup") ||
      option.carrier_code?.toLowerCase().includes("amstorepickup");

    updateBrandShipping(brandId, {
      selectedOptionId: option.id,
      error: null,
      ...(isAmastyOption && baseUrl ? { pickupStores: [], pickupStore: null, pickupError: null } : {}),
      ...(!isAmastyOption ? { pickupStores: [], pickupStore: null, pickupError: null } : {}),
    });
  };

  const handleBrandAmastyStoreSelect = (brandId: string, store: AmastyStoreLocation) => {
    updateBrandShipping(brandId, { pickupStore: store.id });
  };

  const handleBrandCitySelect = async (brandId: string, selectedCity: GeocodingResult) => {
    const retailer = marketplaceRetailers.find((r) => r.retailer_id === brandId);
    if (!retailer?.base_url) return;

    updateBrandShipping(brandId, {
      showPickupCitySelection: false,
      pickupCityCandidates: [],
      loadingPickupStores: true,
      pickupError: null,
      pickupStore: null,
    });

    try {
      const result = await fetchStoresForAmastyShipping(retailer.retailer_id, selectedCity);

      if (result.stores.length > 0) {
        updateBrandShipping(brandId, { pickupStores: result.stores });
      } else {
        updateBrandShipping(brandId, { pickupError: "No stores found in this area" });
      }
    } catch (error) {
      updateBrandShipping(brandId, {
        pickupError: error instanceof Error ? error.message : "Failed to load stores",
      });
    } finally {
      updateBrandShipping(brandId, { loadingPickupStores: false });
    }
  };

  const handleBrandFindNearbyStores = async (brandId: string) => {
    const retailer = marketplaceRetailers.find((r) => r.retailer_id === brandId);
    if (!retailer?.base_url) return;

    updateBrandShipping(brandId, {
      loadingPickupStores: true,
      pickupError: null,
      pickupStore: null,
    });

    const brandState = brandShippingMap.get(brandId) ?? initialBrandShippingState;
    let userLocation: GeocodingResult | undefined = undefined;

    try {
      const gps = await detectUserLocation();

      if (gps.location) {
        userLocation = gps.location;
      } else if (!gps.location && brandState.pickupCity === "") {
        updateBrandShipping(brandId, {
          pickupError:
            "Unable to detect your location. Please allow location access and try again, or enter a city or suburb",
        });
      } else if (!gps.location && brandState.pickupCity !== "") {
        const cities = await getCityCoords(brandState.pickupCity);
        if (cities && cities.length === 1) {
          userLocation = cities[0];
        } else if (cities && cities.length > 1) {
          updateBrandShipping(brandId, {
            pickupCityCandidates: cities,
            showPickupCitySelection: true,
            loadingPickupStores: false,
          });
          return;
        }
      }

      if (userLocation) {
        const result = await fetchStoresForAmastyShipping(retailer.retailer_id, userLocation);

        if (result.stores.length > 0) {
          updateBrandShipping(brandId, { pickupStores: result.stores });
        } else if (!brandState.pickupError) {
          updateBrandShipping(brandId, { pickupError: "No stores found in this area" });
        }
      }
    } catch (error) {
      updateBrandShipping(brandId, {
        pickupError: error instanceof Error ? error.message : "Failed to load stores",
      });
    } finally {
      updateBrandShipping(brandId, { loadingPickupStores: false });
    }
  };

  const handlePickupLocationSelect = (location: PickupLocation) => {
    setSelectedPickupLocationNames({full_name:location.full_name, name: location.name})
    setSelectedPickupLocationId(location.id);
  };

  const handleCitySelect = async (selectedCity: GeocodingResult) => {
    if (!apiConfig?.base_url) return;

    setShowCitySelection(false);
    setCityCandidates([]);
    setLoadingPickupLocations(true);
    setPickupLocationsError(null);
    setSelectedPickupLocationId(null);

    try {
      const result = await fetchStoresForAmastyShipping(theme!.id, selectedCity);

      if (result.stores.length > 0) {
        setPickupLocations(result.stores);
      } else {
        setPickupLocationsError("No stores found in this area");
      }
    } catch (error) {
      setPickupLocationsError(
        error instanceof Error ? error.message : "Failed to load stores",
      );
    } finally {
      setLoadingPickupLocations(false);
    }
  };

  const handleFindNearbyStores = async () => {
    if (!apiConfig || !theme?.id) return;

    setLoadingPickupLocations(true);
    setPickupLocationsError(null);
    setSelectedPickupLocationId(null);

    try {
      if (apiConfig.type === "magento") {
        if (!apiConfig.base_url) return;

        let userLocation: GeocodingResult | undefined = undefined;
        const gps = await detectUserLocation();

        if (gps.location) {
          userLocation = gps.location;
        } else if (pickupCity === "") {
          setPickupLocationsError(
            "Unable to detect your location. Please allow location access and try again, or enter a city or suburb",
          );
        } else {
          const cities = await getCityCoords(pickupCity);
          if (cities && cities.length === 1) {
            userLocation = cities[0];
          } else if (cities && cities.length > 1) {
            setCityCandidates(cities);
            setShowCitySelection(true);
            return;
          }
        }

        if (userLocation) {
          const result = await fetchStoresForAmastyShipping(
            theme.id,
            userLocation,
          );

          if (result.stores.length > 0) {
            setPickupLocations(result.stores);
          } else {
            setPickupLocationsError("No stores found in this area");
          }
        }
      }
    } catch (error) {
      setPickupLocationsError(
        error instanceof Error ? error.message : "Failed to load stores",
      );
    } finally {
      setLoadingPickupLocations(false);
    }
  };

  // Restore automated discount from persisted store on mount
  useEffect(() => {
    if (storedAutomatedDiscountCents > 0) {
      setTotalDiscountsSetCents(storedAutomatedDiscountCents + totalAppliedDiscount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const pickupType = selectedShippingOption?.type;
    const province = formData.province;
    const retailerId = theme?.id;

    if (pickupType?.toLowerCase() !== "store" || !province || !retailerId) {
      return;
    }

    let isCancelled = false;

    const loadPickupLocations = async () => {
      setLoadingPickupLocations(true);
      setPickupLocations([]);
      setPickupLocationsError(null);
      setSelectedPickupLocationId(null);
      setPickupLocationSearch("");

      try {
        const locations = await fetchPickupLocations({
          province,
          retailerId,
          type: pickupType,
        });

        if (isCancelled) return;

        setPickupLocations(
          locations.map((location) => ({
            id: location.id,
            name: location.name,
            full_name: location.full_name,
            address: [
              location.address_1,
              location.address_2,
              location.suburb,
              location.city,
            ]
              .filter(Boolean)
              .join(", "),
          })),
        );

        if (locations.length === 0) {
          setPickupLocationsError(
            "No pickup locations found in this province",
          );
        }
      } catch (error) {
        if (!isCancelled) {
          setPickupLocationsError(
            error instanceof Error
              ? error.message
              : "Failed to load pickup locations",
          );
        }
      } finally {
        if (!isCancelled) {
          setLoadingPickupLocations(false);
        }
      }
    };

    void loadPickupLocations();

    return () => {
      isCancelled = true;
    };
  }, [
    formData.province,
    formData.shipping_option_id,
    selectedShippingOption?.type,
    theme?.id,
  ]);

  async function ConfirmAndUseDiscount(_total: number) {
    if (!discountCode.trim()) return;
    const trimmedCode = discountCode.trim();

    if (isMarketplace) {
      // Marketplace: send all brand shipping details in a single request;
      // the server validates the code across every brand.

      if (!isShippingInfoComplete(formData)) {
        setDiscountError("Please fill in your shipping details before applying a discount code");
        return;
      }

      const missingShippingBrands = getMarketplaceMissingShippingBrands();
      if (missingShippingBrands.length > 0) {
        setDiscountError("Please select a shipping method for all brands before applying a discount code");
        return;
      }

      if (totalDiscountDetail.some((d) => d.code === trimmedCode)) {
        setDiscountError("Code already in use");
        return;
      }

      setDiscountError(null);
      setDiscountWarning(null);
      setDiscountPercentage(null);
      setDiscountRecalculating(true);

      // Build per-brand shipping lines so the server can validate the
      // discount against each brand's selected shipping method.
      const brandShippingLines = Array.from(cartItemsByBrand.keys()).map((brandId) => {
        const brandState = brandShippingMap.get(brandId) ?? initialBrandShippingState;
        const selected = brandState.availableMethods.find(
          (option) =>
            option.id === brandState.selectedOptionId ||
            option.method_code === brandState.selectedOptionId ||
            `shipping-${brandState.availableMethods.indexOf(option)}` === brandState.selectedOptionId,
        );
        return {
          retailerId: brandId,
          title: selected?.name ?? brandState.selectedOptionId ?? "Standard Shipping",
          price: (selected?.price_cents ?? 0) / 100,
          shipping_option_id: brandState.selectedOptionId || undefined,
        };
      });

      try {
        const { data: result } = await validateDiscount.mutateAsync({
          discount_code: trimmedCode,
          retailer_id: theme!.id,
          session_id: sessionId!,
          cart_id: undefined,
          customer_email: formData.email || undefined,
          applied_discounts: totalDiscountDetail.map((d) => d.code),
          shipping_line: brandShippingLines,
          shipping_address: {
            firstName: formData.first_name.trim(),
            lastName: formData.last_name.trim(),
            phone: formData.phone.trim(),
            email: formData.email.trim(),
            address: formData.address.trim(),
            address2: formData.address2?.trim() || undefined,
            suburb: formData.suburb.trim(),
            city: formData.city.trim(),
            postalCode: formData.postal_code.trim(),
            province: formData.province,
          }
        });

        const allEntries = Array.isArray(result.data) ? result.data : [result.data];
        const entries = allEntries.filter((e) => e.valid);
        const draftOrders = result.draftOrders ?? [];

        if (result.combinationResultMessage && result.combinationResultMessage.trim() !== "") {
          setDiscountWarning(result.combinationResultMessage);
        }

        // Sum totalDiscountsSet from successful draft orders
        let totalDiscountsCents = 0;
        for (const draft of draftOrders) {
          if (draft.success && draft.discounts?.total_discounts?.amount) {
            totalDiscountsCents += Math.round(parseFloat(draft.discounts.total_discounts.amount) * 100);
          }
        }
        setTotalDiscountsSetCents(totalDiscountsCents);

        // Flatten newDiscounts (array of arrays in marketplace response)
        if (result.newDiscounts) {
          const flatNewDiscounts = Array.isArray(result.newDiscounts[0])
            ? (result.newDiscounts as string[][]).flat()
            : (result.newDiscounts as string[]);
          validDiscountCodesRef.current = flatNewDiscounts;
        }

        // Create one DiscountDetail per valid brand (ignore failed ones)
        for (const entry of entries) {
          const hasCg =
            entry.customerGets !== undefined &&
            Array.isArray(entry.customerGets) &&
            entry.customerGets.length > 0;

          // Initial totalValue = 0; the recalc useEffect will compute the
          // per-brand value from that brand's cart items only.
          const detail: DiscountDetail = {
            code: trimmedCode,
            brandId: entry.brand_id,
            discountValue: entry.discountValue ?? 0,
            totalValue: 0,
            discountsOn: [],
            type: entry.DiscountType ?? "",
            ineligibleItems: entry.ineligible_items,
            customerGets: hasCg ? entry.customerGets as CustomerGets[] : undefined,
            codeType: entry.CodeType,
            isShippingDiscount: entry.DiscountType === "DiscountCodeFreeShipping",
          };

          addDiscount(detail);
        }

        if (entries.length > 0) {
          await queryClient.invalidateQueries({ queryKey: ["cart", sessionId] });
          setDiscountCode("");
          setDiscountAppliedAt(Date.now());
        } else {
          const errorMsg = allEntries.find((e) => !e.valid)?.message || result.combinationResultMessage || "Invalid code";
          setDiscountError(errorMsg);
          setDiscountRecalculating(false);
        }
      } catch (error) {
        console.error("Failed to validate marketplace discount:", error);
        setDiscountError("Invalid code");
        setDiscountRecalculating(false);
        if (sessionId) {
          trackCheckoutError(
            sessionId,
            "discount_usage",
            error instanceof Error ? error.message : "Marketplace discount validation failed",
            { discount_code: trimmedCode, retailer_id: theme?.id ?? null },
          );
        }
        if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
          Sentry.captureException(error, {
            tags: { component: "OrderPage", action: "validateMarketplaceDiscount", discountCode: trimmedCode },
          });
        }
      }
      return;
    }

    // Non-marketplace: original single-retailer flow
    if (totalDiscountDetail.some((d) => d.code === trimmedCode)) {
      setDiscountError("Code already in use");
      return;
    }

    setDiscountError(null);
    setDiscountWarning(null);
    setDiscountPercentage(null);
    setDiscountRecalculating(true);

    try {
      const { data: result } = await validateDiscount.mutateAsync({
        discount_code: trimmedCode,
        retailer_id: theme!.id,
        session_id: sessionId!,
        cart_id: magentoCartId || undefined,
        customer_email: formData.email || undefined,
        applied_discounts: totalDiscountDetail.map((d) => d.code),
        shipping_line: selectedShippingOption ? { title: selectedShippingOption.name, price: (selectedShippingOption.price_cents ?? 0) / 100 } : undefined,
        shipping_address: formData.shipping_option_id ? {
          firstName: formData.first_name.trim(),
          lastName: formData.last_name.trim(),
          phone: formData.phone.trim(),
          email: formData.email.trim(),
          address: formData.address.trim(),
          address2: formData.address2?.trim() || undefined,
          suburb: formData.suburb.trim(),
          city: formData.city.trim(),
          postalCode: formData.postal_code.trim(),
          province: formData.province,
        } : undefined,
        shipping_option_id: formData.shipping_option_id || undefined,
      });

      // Non-marketplace always returns a single entry; narrow the union
      const inner = Array.isArray(result.data) ? result.data[0]! : result.data;

      if (inner.valid) {
        const rawDiscountValue = inner.discountValue ?? 0;

        const tds = result.totalDiscountsSet;
        if (tds && typeof tds === "object" && tds.amount) {
          setTotalDiscountsSetCents(Math.round(parseFloat(tds.amount) * 100));
        }
        if (result.combinationResultMessage && result.combinationResultMessage.trim() !== "") {
          setDiscountWarning(result.combinationResultMessage);
        }

        const hasCg =
          inner.customerGets !== undefined &&
          Array.isArray(inner.customerGets) &&
          inner.customerGets.length > 0;

        // Initial totalValue = 0; the recalc useEffect will compute the
        // per-code value from cart items so the automated-discount gap
        // (totalDiscountsSetCents − totalAppliedDiscount) stays accurate.
        const detail: DiscountDetail = {
          code: trimmedCode,
          discountValue: rawDiscountValue,
          totalValue: 0,
          discountsOn: [],
          type: inner.DiscountType ?? "",
          ineligibleItems: inner.ineligible_items,
          customerGets: hasCg ? inner.customerGets as CustomerGets[] : undefined,
          codeType: inner.CodeType,
          isShippingDiscount: inner.DiscountType === "DiscountCodeFreeShipping",
        };

        // Trigger recalc after cart refetch
        await queryClient.invalidateQueries({
            queryKey: ["cart", sessionId],
          });

        addDiscount(detail);
        if (inner.newDiscounts) {
          validDiscountCodesRef.current = inner.newDiscounts;
        }
        setDiscountCode("");
        setDiscountAppliedAt(Date.now());
      } else {
        const errorMsg = inner.message || result.combinationResultMessage || "Invalid code";
        setDiscountError(errorMsg);
        setDiscountRecalculating(false);
      }
    } catch (error) {
      console.error("Failed to validate discount code:", error);
      setDiscountError("Invalid code");
      setDiscountRecalculating(false);
      if (sessionId) {
        trackCheckoutError(
          sessionId,
          "discount_usage",
          error instanceof Error ? error.message : "Discount validation failed",
          { discount_code: trimmedCode, retailer_id: theme?.id ?? null },
        );
      }
      if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
        Sentry.captureException(error, {
          tags: {
            component: "OrderPage",
            action: "validateDiscountCode",
            discountCode: trimmedCode,
          },
        });
      }
    }
  }

  async function HandleSubmitOrder() {
    if (!validateForm() || !sessionId || !cart) return;
    setOrderError(null);
    setIsPrefilPending(true);

    // Build discount codes and total
    const discountCodes = [...new Set(totalDiscountDetail.map((d) => d.code))];
    const discountTotalCents = totalAppliedDiscount + automatedDiscountCents;

    // Build shipping and marketplace-specific fields
    let brandShippingQuotes: BrandShippingQuote[] | undefined;
    let brandDiscounts:
      | { brand_id: string; discount_code: string; discount_total_cents: number }[]
      | undefined;
    let shippingOptionId: string;
    let shippingName: string;
    let shippingPriceCents: number;

    if (isMarketplace) {
      brandShippingQuotes = [];
      for (const [brandId] of cartItemsByBrand) {
        const brandState = brandShippingMap.get(brandId) ?? initialBrandShippingState;
        const selected = brandState.availableMethods.find(
          (option) =>
            option.id === brandState.selectedOptionId ||
            option.method_code === brandState.selectedOptionId ||
            `shipping-${brandState.availableMethods.indexOf(option)}` === brandState.selectedOptionId,
        );
        brandShippingQuotes.push({
          brand_id: brandId,
          shipping_option_id: brandState.selectedOptionId || "no-shipping-selected",
          shipping_name: selected?.name || brandState.selectedOptionId || "Standard Shipping",
          shipping_price_cents: selected?.price_cents || 0,
          estimated_days: selected?.estimated_days ?? undefined,
          brand_cart_id: magentoCartIds[brandId] ?? null,
        });
      }

      brandDiscounts = totalDiscountDetail
        .filter((d) => d.brandId && d.totalValue > 0)
        .map((d) => ({
          brand_id: d.brandId!,
          discount_code: d.code,
          discount_total_cents: d.totalValue,
        }));

      shippingOptionId = "marketplace";
      shippingName = "Marketplace Shipping";
      shippingPriceCents = marketplaceShippingCents;
    } else {
      shippingOptionId =
        selectedShippingOption?.id ||
        selectedShippingOption?.method_code ||
        "no-shipping-selected";
      shippingName =
        selectedShippingOption?.name ||
        selectedShippingOption?.id ||
        "Standard Shipping";
      shippingPriceCents = shippingCents;
    }

    // Sync draft order with shipping & discount details before prefil.
    // Check if market place has a shopfiy custom integrated retailer (shopify & hybrid).
    // Single retailer, only shopify, not hybrid as solo hybrid goes to shopify checkout.
    const draftIntegrationType = apiConfig?.type?.toLowerCase();
    const shouldSkipDraftOrder = isMarketplace
      ? !marketplaceRetailers.some(
          (r) => ["shopify", "shopify_hybrid"].includes(r.integration_type?.toLowerCase() ?? ""),
        )
      : draftIntegrationType !== "shopify";

    if (!shouldSkipDraftOrder) {
      const appliedCodes =
        discountCodes.length > 0 ? discountCodes.join(",") : undefined;

      let draftShippingLine:
        | { title: string; price: number }
        | { retailerId: string; title: string; price: number }[]
        | undefined;

      if (isMarketplace) {
        draftShippingLine = Array.from(cartItemsByBrand.keys()).map(
          (brandId) => {
            const brandState =
              brandShippingMap.get(brandId) ?? initialBrandShippingState;
            const selected = brandState.availableMethods.find(
              (option) =>
                option.id === brandState.selectedOptionId ||
                option.method_code === brandState.selectedOptionId ||
                `shipping-${brandState.availableMethods.indexOf(option)}` === brandState.selectedOptionId,
            );
            return {
              retailerId: brandId,
              title:
                selected?.name ??
                brandState.selectedOptionId ??
                "Standard Shipping",
              price: (selected?.price_cents ?? 0) / 100,
            };
          },
        );
      } else {
        draftShippingLine = selectedShippingOption
          ? {
              title: selectedShippingOption.name,
              price: (selectedShippingOption.price_cents ?? 0) / 100,
            }
          : undefined;
      }

      createDraftOrder.mutate({
        request: {
          session_id: sessionId,
          retailer_id: theme!.id,
          customer_email: formData.email || undefined,
          cart_id: magentoCartId || undefined,
          discount_code: appliedCodes,
          applied_discounts:
            discountCodes.length > 0 ? discountCodes : undefined,
          shipping_line: draftShippingLine,
          shipping_address: {
            firstName: formData.first_name.trim(),
            lastName: formData.last_name.trim(),
            phone: formData.phone.trim(),
            email: formData.email.trim(),
            address: formData.address.trim(),
            address2: formData.address2?.trim() || undefined,
            suburb: formData.suburb.trim(),
            city: formData.city.trim(),
            postalCode: formData.postal_code.trim(),
            province: formData.province,
          },
          shipping_option_id: formData.shipping_option_id || undefined,
        },
        method: "PUT",
      });
    }

    // prefil order for needed metadata
    try {
      const data = await prefilOrderMutation.mutateAsync({
        session_id: sessionId,
        customer_id: sessionData?.session?.customer_id ?? null,
        retailer_id: theme?.id ?? null,
        store_pickup_id: selectedPickupLocationId,
        store_pickup_names: selectedPickupLocationNames,
        shipping_info: {
          city: formData.city.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          suburb: formData.suburb.trim(),
          address: formData.address.trim(),
          address2: formData.address2?.trim() || undefined,
          province: formData.province,
          last_name: formData.last_name.trim(),
          first_name: formData.first_name.trim(),
          postal_code: formData.postal_code.trim(),
        },
        discount_codes: discountCodes.length > 0 ? discountCodes : null,
        discount_total_cents: discountTotalCents,
        shipping_option_id: shippingOptionId,
        shipping_name: shippingName,
        shipping_price_cents: shippingPriceCents,
        update_contact_details: updateContactDetails,
        update_shipping_details: updateShippingDetails,
        brand_shipping_quotes: brandShippingQuotes,
        brand_discounts:
          brandDiscounts && brandDiscounts.length > 0 ? brandDiscounts : undefined,
        external_cart_id: isMarketplace ? null : magentoCartId || null,
      });

      if(data.order_id){
        // Persist order details for PaymentPage to consume (before navigate)
        const selectedPickupLocation = selectedPickupLocationId
          ? pickupLocations.find(
              (location) => location.id === selectedPickupLocationId,
            )
          : null;

        const shippingDetailsForStore: ShippingInfo = {
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          phone: formData.phone.trim(),
          email: formData.email.trim(),
          address: formData.address.trim(),
          address2: formData.address2?.trim() || undefined,
          suburb: formData.suburb.trim(),
          city: formData.city.trim(),
          postal_code: formData.postal_code.trim(),
          province: formData.province,
          ...(selectedPickupLocation
            ? { store_pickup: selectedPickupLocation.name }
            : {}),
        };

        const shipmentsForStore: MarketplaceOrderShipping[] = [];
        if (isMarketplace) {
          for (const [brandId, items] of cartItemsByBrand) {
            const brandState = brandShippingMap.get(brandId) ?? initialBrandShippingState;
            const selected = brandState.availableMethods.find(
              (option) =>
                option.id === brandState.selectedOptionId ||
                option.method_code === brandState.selectedOptionId ||
                `shipping-${brandState.availableMethods.indexOf(option)}` === brandState.selectedOptionId,
            );
            const brandCartId = magentoCartIds[brandId] ?? null;
            const pickupStore = brandState.pickupStore
              ? brandState.pickupStores.find((s) => s.id === brandState.pickupStore)
              : null;

            shipmentsForStore.push({
              child_retailer_id: brandId,
              shipping_option_id: brandState.selectedOptionId || "no-shipping-selected",
              shipping_name: selected?.name || brandState.selectedOptionId || "Standard Shipping",
              shipping_price_cents: selected?.price_cents || 0,
              carrier_code: selected?.carrier_code,
              method_code: selected?.method_code,
              store_pickup: pickupStore?.name,
              brand_cart_id: brandCartId,
              item_ids: items.map((i) => i.id),
            });
          }
        }

        setOrderDetails({
          isMarketplace,
          shippingDetails: shippingDetailsForStore,
          cartItemsByBrand: Array.from(cartItemsByBrand.entries()),
          marketplaceRetailers,
          totalDiscountDetail,
          totalAppliedDiscount,
          automatedDiscountCents,
          selectedShippingOption: selectedShippingOption ?? null,
          selectedPickupLocationId,
          apiConfig,
          shippingCents,
          paymentSubtotalCents: null,
          paymentShippingCents: null,
          paymentDiscountCents: null,
          paymentTaxCents: null,
          paymentTotalCents: null,
          themeId: theme?.id ?? null,
          shipments: shipmentsForStore,
        });

        navigate(`/order/${sessionId}/pay?order_id=${data.order_id}`);
      } else {
        console.error("[OrderPage - checkoutFetchError] - No order_id returned");
        setOrderError("Failed to set initial order details");
        if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
          Sentry.captureException(new Error("No order_id returned from prefil"), {
            tags: { component: "OrderPage", action: "createOrder", sessionId },
          });
        }
      }
    } catch (error) {
      console.error("[OrderPage - checkoutFetchError] -", error);
      if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
        Sentry.captureException(error, {
          tags: { component: "OrderPage", action: "createOrder", sessionId },
          extra: { message: "Failed to prefill order" },
        });
      }
      setOrderError("Failed to set initial order details");
    } finally {
      setIsPrefilPending(false);
    }
  }

  const handleRemoveDiscount = (code: string, brandId?: string) => {
    if (brandId !== undefined) {
      removeDiscount(code, brandId); // Only useful for marketplace
    } else {
      removeDiscountsByCode(code);
    }
    const remaining = totalDiscountDetail.filter(
      (d) => !(d.code === code && (brandId === undefined || d.brandId === brandId)),
    );
    if (remaining.length === 0) {
      clearDiscountStorage();
      clearDiscountStore();
      setTotalDiscountsSetCents(0);
      setDiscountCode("");
      setDiscountPercentage(null);
      setDiscountError(null);
      setDiscountWarning(null);
    }
  };

  // -------------------------------------------------------------------------
  //#endregion
  // -------------------------------------------------------------------------

  // -------------------------------------------------------------------------
  //#region E. Marketplace helpers
  // -------------------------------------------------------------------------

  function updateBrandShipping(brandId: string, updates: Partial<BrandShippingState>) {
    setBrandShippingMap((prev) => {
      const next = new Map(prev);
      const existing = next.get(brandId) ?? { ...initialBrandShippingState };
      next.set(brandId, { ...existing, ...updates });
      return next;
    });
  }

  function getMarketplaceMissingShippingBrands(): string[] {
    const missing: string[] = [];
    for (const [brandId, _items] of cartItemsByBrand) {
      const retailer = marketplaceRetailers.find((r) => r.retailer_id === brandId);
      const parentRequires = enforceShipping;
      const childRequires = retailer?.enforce_shipping ?? false;
      const brandState = brandShippingMap.get(brandId) ?? initialBrandShippingState;
      if ((parentRequires || childRequires) && !brandState.selectedOptionId) {
        missing.push(brandId);
      }
    }
    return missing;
  }

  function getMarketplaceSelectedMethod(brandId: string): ShippingMethod | undefined {
    const brandState = brandShippingMap.get(brandId) ?? initialBrandShippingState;
    return brandState.availableMethods.find(
      (option) =>
        option.id === brandState.selectedOptionId ||
        option.method_code === brandState.selectedOptionId ||
        `shipping-${brandState.availableMethods.indexOf(option)}` === brandState.selectedOptionId,
    );
  }

  function getBrandName(brandId: string): string {
    const retailer = marketplaceRetailers.find((r) => r.retailer_id === brandId);
    if (retailer?.name) return retailer.name;
    const firstItem = cartItemsByBrand.get(brandId)?.[0];
    return firstItem?.fulfillment_brand_name || "Brand";
  }

  // -------------------------------------------------------------------------
  //#endregion
  // -------------------------------------------------------------------------

  // -------------------------------------------------------------------------
  //#region F. useEffect hooks (grouped by concern)
  // -------------------------------------------------------------------------

  // Redirect to payment if pending order and empty cart
  useEffect(() => {
    if (
      hasEmptyCart &&
      orders &&
      orders.orders.length > 0 &&
      sessionId &&
      !hasCheckedOrder.current
    ) {
      const pendingOrder = orders.orders.find((o) => o.status === "pending");
      hasCheckedOrder.current = true;
      if (pendingOrder) {
        navigate(`/order/${sessionId}/pay?order_id=${pendingOrder.id}`);
      }
    }
  }, [hasEmptyCart, orders, sessionId, navigate]);

  // Measure mobile bottom bar height to keep spacer in sync
  useEffect(() => {
    const el = mobileBottomBarRef.current;
    if (!el) return;
    const measure = () => setMobileBarHeight(el.offsetHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [showSummary]);

  // Detect empty cart
  useEffect(() => {
    if (cart && !cartLoading && cart.items.length === 0) {
      setHasEmptyCart(true);
    }
  }, [cart, cartLoading]);

  // Set marketplace mode from retailer config and sync child retailers
  useEffect(() => {
    if (marketplaceSession?.retailers) {
      setMarketplaceRetailers(marketplaceSession.retailers);
    }
  }, [marketplaceSession]);

  // Initialize marketplace shipping state when cart/retailers are available
  useEffect(() => {
    if (!isMarketplace || !cart || cart.items.length === 0) return;

    setBrandShippingMap((prev) => {
      const next = new Map(prev);
      for (const brandId of cartItemsByBrand.keys()) {
        if (!next.has(brandId)) {
          next.set(brandId, { ...initialBrandShippingState });
        }
      }
      // Remove stale brands no longer in the cart
      for (const key of next.keys()) {
        if (!cartItemsByBrand.has(key)) {
          next.delete(key);
        }
      }
      return next;
    });
  }, [isMarketplace, cart, cartItemsByBrand]);

  // Fetch marketplace dynamic shipping methods per brand
  useEffect(() => {
    async function fetchMarketplaceShipping() {
      if (!isMarketplace || !cart || !sessionId || !formData.province) return;

      for (const brandId of cartItemsByBrand.keys()) {
        const retailer = marketplaceRetailers.find((r) => r.retailer_id === brandId);
        if (!retailer) continue;

        const brandState = brandShippingMap.get(brandId) ?? initialBrandShippingState;
        if (brandState.loading) continue;

        const items = cartItemsByBrand.get(brandId) || [];
        const brandCartId = magentoCartIds[brandId] ?? null;
        const brandSubtotalCents = items.reduce(
          (sum, item) => sum + item.unit_price_cents * item.quantity,
          0,
        );

        try {
          updateBrandShipping(brandId, { loading: true, error: null });
          let methods: ShippingMethod[] = [];

          // Static methods from retailer config, filtered by brand subtotal
          if (retailer.shipping_methods && retailer.shipping_methods.length > 0) {
            methods = retailer.shipping_methods.filter((method) => {
              if (method.countries && !method.countries.includes("South Africa")) {
                return false;
              }
              const provinceMatch =
                !method.provinces ||
                method.provinces.length === 0 ||
                method.provinces.some(
                  (p) => p.toLowerCase() === formData.province.toLowerCase(),
                );
              const conditions = areConditionsMet(method, brandSubtotalCents);
              return provinceMatch && conditions;
            });
          }

          // BobGo dynamic rates
          if (retailer.bobgo && isShippingInfoComplete(formData)) {
            const bobgoMethods = await fetchBobgoShipping(sessionId, formData, brandId);
            const mapped = bobgoMethods.map((method) => ({
              name: method.service_name,
              description: `${method.description}. Estimated delivery dates: ${method.min_delivery_date} to ${method.max_delivery_date}.`,
              price_cents: Math.round(method.total_price * 100),
              estimated_days: Math.max(
                0,
                Math.round(
                  (new Date(method.max_delivery_date).getTime() - new Date(method.min_delivery_date).getTime()) /
                    (1000 * 60 * 60 * 24),
                ),
              ),
            }));
            methods = [...methods.filter((m) => !mapped.some((bm) => bm.name === m.name)), ...mapped];
          }

          // Magento dynamic rates
          if (retailer.integration_type === "magento" && brandCartId && isShippingInfoComplete(formData)) {
            const magentoMethods = await fetchMagentoShipping(brandId, brandCartId, formData);
            methods = magentoMethods.map((method) => ({
              carrier_code: method.carrier_code,
              method_code: method.method_code,
              name: method.carrier_title,
              description: method.method_title,
              price_cents: Math.round(method.price_incl_tax * 100),
              estimated_days: 0,
            }));
          }

          updateBrandShipping(brandId, { availableMethods: methods, loading: false });
        } catch (error) {
          console.error(`Failed to fetch marketplace shipping for brand ${brandId}:`, error);
          updateBrandShipping(brandId, {
            loading: false,
            error: error instanceof Error ? error.message : "Failed to load shipping",
          });
        }
      }
    }

    fetchMarketplaceShipping();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isMarketplace,
    marketplaceRetailers,
    cart,
    sessionId,
    formData.first_name,
    formData.last_name,
    formData.phone,
    formData.email,
    formData.address,
    formData.address2,
    formData.suburb,
    formData.city,
    formData.postal_code,
    formData.province,
  ]);

  // GTM checkout_started event
  useEffect(() => {
    if (sessionData?.session?.context?.attribution) {
      try {
        window.dataLayer?.push({
          event: "checkout_started",
          link_id: sessionData.session.context.attribution.linkId,
          utm_source: sessionData.session.context.attribution.utmSource,
          utm_medium: sessionData.session.context.attribution.utmMedium,
          utm_campaign: sessionData.session.context.attribution.utmCampaign,
        });
      } catch (error) {
        console.error("GTM checkout_started error:", error);
        if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
          Sentry.captureException(error, {
            tags: { component: "OrderPage", action: "gtmCheckoutStarted" },
          });
        }
      }
    }
  }, [sessionData]);

  // Prefill form from session customer data
  useEffect(() => {
    if (sessionData?.session?.customer) {
      const fullName = sessionData?.session?.customer?.name || "";
      const nameParts = fullName.trim().split(" ");
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";
      const customer = sessionData.session.customer;

      if (customer.shipping_details) {
        setIsFormPrefilled(true);
      }

      setFormData((prev) => ({
        ...prev,
        first_name: firstName || "",
        last_name: lastName || "",
        email: customer.email || "",
        phone: customer.phone_number || "",
        address: customer.shipping_details?.address || "",
        suburb: hasCumiIntegration
          ? ""
          : customer.shipping_details?.suburb || "",
        city: hasCumiIntegration ? "" : customer.shipping_details?.city || "",
        postal_code: hasCumiIntegration
          ? ""
          : customer.shipping_details?.postal_code || "",
        province: hasCumiIntegration
          ? ""
          : customer.shipping_details?.province || "",
      }));
      if (hasCumiIntegration) {
        setSelectedCumiFareyeCode("");
      }
    }
  }, [hasCumiIntegration, sessionData?.session]);

  // Synchronize the current session's Cumi customer ID before creating one.
  useEffect(() => {
    if (!hasCumiIntegration || !sessionId || !sessionData?.session) return;
    if (cumiMetadataSyncedSessionRef.current === sessionId) return;

    const metadataId =
      sessionData.session.customer?.metadata?.cumi_customer_id;
    const customerId =
      typeof metadataId === "string" && metadataId.trim()
        ? metadataId.trim()
        : null;

    cumiMetadataSyncedSessionRef.current = sessionId;
    setCumiCustomerId(customerId);
    localStorage.setItem("cumi_customer_id", customerId ?? "");
  }, [hasCumiIntegration, sessionData?.session, sessionId]);

  // After the local metadata check misses, search Oracle, then create.
  useEffect(() => {
    const firstName = formData.first_name.trim();
    const lastName = formData.last_name.trim();
    const phone = formData.phone.trim();
    const email = formData.email.trim();
    const hasValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    if (
      !hasCumiIntegration ||
      cumiCustomerId !== null ||
      !firstName ||
      !lastName ||
      !validateSAPhone(phone) ||
      !hasValidEmail
    ) {
      return;
    }

    const abortController = new AbortController();
    const timeoutId = window.setTimeout(async () => {
      try {
        const search = await searchCumiCustomer(
          { phone },
          abortController.signal,
        );

        if (search.customer_found) {
          setCumiCustomerId(search.customer_id);
          localStorage.setItem("cumi_customer_id", search.customer_id);
          return;
        }

        const response = await createCumiCustomer(
          { phone, firstName, lastName, email },
          abortController.signal,
        );
        const customerId = response.data.customer_id.trim();

        if (!response.data.success || !customerId) {
          throw new Error("Cumi customer creation was unsuccessful");
        }

        setCumiCustomerId(customerId);
        localStorage.setItem("cumi_customer_id", customerId);
      } catch (error) {
        if (abortController.signal.aborted) return;
        if (sessionId) {
          trackCheckoutError(
            sessionId,
            "cumi_cart_creation",
            error instanceof Error ? error.message : "Cumi customer creation failed",
            { retailer_id: theme?.id ?? null },
          );
        }
        if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
          Sentry.captureException(error, {
            tags: {
              component: "OrderPage",
              action: "createCumiCustomer",
            },
          });
        }
      }
    }, 2000);

    return () => {
      window.clearTimeout(timeoutId);
      abortController.abort();
    };
  }, [
    cumiCustomerId,
    formData.email,
    formData.first_name,
    formData.last_name,
    formData.phone,
    hasCumiIntegration,
    sessionId,
    theme?.id,
  ]);

  // Fetch retailer config & shipping methods
  useEffect(() => {
    const loadRetailerConfig = async () => {
      if (!theme?.id) return;
      try {
        const retailer = await fetchRetailerConfig(theme.id);
        const shippingMethods = Object.values(
          retailer?.config?.shipping_methods || {},
        );
        const integrationConfig = await fetchIntegrationConfig(theme.id);
        if (integrationConfig) {
          setApiConfig(integrationConfig);
        }

        setEnforceShipping(retailer?.config?.enforce_shipping || true);
        setIsMarketplace(retailer?.isMarketplace === true);

        // Check if session has shipping context and filter to show only matching method
        const sessionShippingName = sessionData?.session?.context?.shipping;
        if (!retailer?.isMarketplace && sessionShippingName && shippingMethods.length > 0) {
          const matchedMethod = shippingMethods.find(
            (method) =>
              method.name.toLowerCase() === sessionShippingName.toLowerCase(),
          );
          if (matchedMethod) {
            // Show only the matching shipping method
            // shippingMethods = [matchedMethod];
            const optionId =
              matchedMethod.id ||
              `shipping-${matchedMethod.name?.toLowerCase().replace(/\s+/g, "-")}`;
            setFormData((prev) => ({
              ...prev,
              shipping_option_id: optionId,
            }));
            localStorage.setItem(
              "selectedShippingMethod",
              JSON.stringify({
                id: optionId,
                name: matchedMethod.name,
                description: matchedMethod.description,
                price_cents: matchedMethod.price_cents,
              }),
            );
          }
        }

        setShippingData(shippingMethods);
      } catch (error) {
        console.error("Failed to fetch retailer config", error);
      }
    };
    loadRetailerConfig();
  }, [theme?.id, sessionData?.session?.context?.shipping]);

  // Fetch dynamic shipping methods (Magento / Bobgo)
  useEffect(() => {
    async function fetchShippingMethods() {
      if (!theme || !apiConfig) return;
      const integrationType = apiConfig?.type;

      switch (integrationType) {
        case "shopify": {
          if (apiConfig.bobgo && isShippingInfoComplete(formData)) {
            setCalculatingShipping(true);
            const bobgoMethods = await fetchBobgoShipping(sessionId!, formData);
            const mappedMethods: ShippingMethod[] = bobgoMethods.map(
              (method) => ({
                name: method.service_name,
                description: `${method.description}. Estimated delivery dates: ${method.min_delivery_date} to ${method.max_delivery_date}.`,
                price_cents: Math.round(method.total_price * 100),
              }),
            );
            setShippingData((prev) => {
              const nonBobgo = prev.filter(
                (m) => !mappedMethods.some((bm) => bm.name === m.name),
              );
              return [...nonBobgo, ...mappedMethods];
            });
            setCalculatingShipping(false);
          }
          break;
        }
        case "woocommerce":
          break;
        case "cumi":
          break;
        case "magento": {
          if (!magentoCartId || !isShippingInfoComplete(formData)) return;
          try {
            setCalculatingShipping(true);
            const magentoMethods = await fetchMagentoShipping(
              theme.id,
              magentoCartId,
              formData,
            );
            const mappedMethods: ShippingMethod[] = magentoMethods.map(
              (method) => ({
                carrier_code: method.carrier_code,
                method_code: method.method_code,
                name: method.carrier_title,
                description: method.method_title,
                price_cents: Math.round(method.price_incl_tax * 100),
              }),
            );
            setShippingData(mappedMethods);
          } catch (error) {
            console.error("Failed to fetch Magento shipping methods:", error);
          } finally {
            setCalculatingShipping(false);
          }
          break;
        }
        case "csv":
          break;
        case "custom_rest":
          break;
      }
    }

    fetchShippingMethods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    theme,
    apiConfig,
    magentoCartId,
    formData.first_name,
    formData.last_name,
    formData.phone,
    formData.email,
    formData.address,
    formData.address2,
    formData.suburb,
    formData.city,
    formData.postal_code,
    formData.province,
  ]);

  // Recalculate discount totalValue and discountsOn for each discount in the store
  useEffect(() => {
    const discounts = totalDiscountDetailRef.current;
    if (cartLoading === false && cart && discounts.length > 0) {
      for (const d of discounts) {
        // Automated discounts come from the server draft order — don't recalculate
        if (d.type === "AutomatedDiscount") continue;

        let totalValue = 0;
        let discountsOn: string[] = [];

        if (d.isShippingDiscount) {
          // For marketplace brand-scoped shipping discounts, use only that
          // brand's selected shipping method price. For non-marketplace, use
          // the overall shipping cost.
          if (d.brandId && isMarketplaceRef.current) {
            const brandState = brandShippingMapRef.current.get(d.brandId) ?? initialBrandShippingState;
            const brandMethod = brandState.availableMethods.find(
              (opt) =>
                opt.id === brandState.selectedOptionId ||
                opt.method_code === brandState.selectedOptionId ||
                `shipping-${brandState.availableMethods.indexOf(opt)}` === brandState.selectedOptionId,
            );
            totalValue = brandMethod ? brandMethod.price_cents ?? 0 : 0;
          } else {
            totalValue = selectedShippingOption ? shippingCents : 0;
          }
          updateDiscount(d.code, { totalValue }, d.brandId);
          continue;
        }

        // Scope items to brand when discount is brand-specific (marketplace)
        const scopedItems = d.brandId
          ? cart.items.filter((c) => c.fulfillment_brand_id === d.brandId)
          : cart.items;

        const ineligibleItems = d.ineligibleItems ?? [];
        const isItemIneligible = (item: { id?: string; product_id: string }) =>
          ineligibleItems.some(
            (inc) => inc.cart_id === item.id || inc.product_id === item.product_id,
          );

        if (d.type === "DiscountCodeBasic" && d.customerGets && d.customerGets.length > 0) {
          // Product-specific discount
          const matchedItems = scopedItems.filter(
            (c) =>
              !isItemIneligible(c) &&
              d.customerGets!.some((cg) => (cg.id || cg.cart_id) == c.id),
          );
          discountsOn = matchedItems.map((c) => c.id);
          const isFlatRate = d.discountValue > 1;
          totalValue = matchedItems.reduce((sum, item) => {
            return isFlatRate
              ? sum + item.quantity * (d.discountValue * 100)
              : sum + item.line_total_cents * d.discountValue;
          }, 0);
        } else if (d.type === "DiscountCodeBasic" || d.type === "Magento") {
          // Order-level discount
          const eligibleItems = scopedItems.filter((c) => !isItemIneligible(c));
          discountsOn = eligibleItems.map((c) => c.id);
          if (d.codeType === "DiscountPercentage") {
            const eligibleSubtotal = eligibleItems.reduce(
              (sum, c) => sum + c.line_total_cents,
              0,
            );
            totalValue = Math.round(eligibleSubtotal * d.discountValue);
          } else {
            // Flat rate
            totalValue = d.discountValue > 1
              ? Math.round(d.discountValue * 100)
              : Math.round(d.discountValue);
          }
        } else if (d.type === "DiscountCodeBxgy") {
          // BxGy discount
          const isSameProduct = d.customerGets?.some((cg) => "discountedQTY" in cg);

          if (isSameProduct) {
            let totalDiscount = 0;
            for (const cg of d.customerGets ?? []) {
              if (!("discountedQTY" in cg)) continue;
              const cartItem = scopedItems.find(
                (ci) => ci.product_id === cg.product_id && !isItemIneligible(ci),
              );
              if (cartItem) {
                discountsOn.push(cartItem.id);
                if (d.discountValue > 1) {
                  totalDiscount += Math.round(d.discountValue * 100 * cg.discountedQTY);
                } else {
                  const unitPrice = cartItem.line_total_cents / cartItem.quantity;
                  totalDiscount += Math.round(cg.discountedQTY * unitPrice * d.discountValue);
                }
              }
            }
            totalValue = totalDiscount;
          } else {
            // Different product BxGy
            let discountedProduct = scopedItems.filter(
              (ci) =>
                !isItemIneligible(ci) &&
                d.customerGets?.some((cg) => "quantity" in cg && cg.id == ci.id),
            );

            if (discountedProduct.length < 1) {
              discountedProduct = scopedItems.filter(
                (ci) =>
                  !isItemIneligible(ci) &&
                  d.customerGets?.some((cg) => "quantity" in cg && cg.cart_id == ci.id),
              );
            }

            discountsOn = discountedProduct.map((ci) => ci.id);

            if (discountedProduct.length > 0 && d.discountValue) {
              let totalDiscount = 0;
              for (const dp of discountedProduct) {
                const cgEntry = d.customerGets?.find(
                  (cg) =>
                    "quantity" in cg &&
                    ((cg.id && cg.id == dp.id) || (cg.cart_id && cg.cart_id == dp.id)),
                );
                const qty = cgEntry && "quantity" in cgEntry ? cgEntry.quantity : 1;
                const itemDiscount =
                  d.discountValue > 1
                    ? Math.round(d.discountValue * 100 * qty)
                    : Math.round(dp.line_total_cents * d.discountValue);
                totalDiscount += itemDiscount;
              }
              totalValue = totalDiscount;
            }
          }
        }

        updateDiscount(d.code, { totalValue: Math.round(totalValue), discountsOn }, d.brandId);
      }

      // Filter out discounts no longer in the server's valid list
      const validCodes = validDiscountCodesRef.current;
      if (validCodes.length > 0) {
        const staleCodes = discounts
          .filter((d) => !validCodes.includes(d.code))
          .map((d) => d.code);
        for (const code of staleCodes) {
          removeDiscountsByCode(code);
        }
        validDiscountCodesRef.current = [];
      }

      setDiscountRecalculating(false);
    } else if (cartLoading === false) {
      // Cart refetch failed or returned empty — unblock the submit button
      // so the customer is not permanently stuck on "Processing…".
      setDiscountRecalculating(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartLoading, cart, discountAppliedAt, selectedShippingOption, shippingCents]);

  // -------------------------------------------------------------------------
  //#endregion
  // -------------------------------------------------------------------------

  // -------------------------------------------------------------------------
  //#region G. Validation helpers
  // -------------------------------------------------------------------------

  const isShippingInfoComplete = (data: ShippingFormData): boolean => {
    return (
      typeof data.first_name === "string" &&
      data.first_name.trim() !== "" &&
      typeof data.last_name === "string" &&
      data.last_name.trim() !== "" &&
      typeof data.phone === "string" &&
      data.phone.trim() !== "" &&
      typeof data.email === "string" &&
      data.email.trim() !== "" &&
      typeof data.address === "string" &&
      data.address.trim() !== "" &&
      typeof data.suburb === "string" &&
      data.suburb.trim() !== "" &&
      typeof data.city === "string" &&
      data.city.trim() !== "" &&
      typeof data.postal_code === "string" &&
      data.postal_code.trim() !== "" &&
      typeof data.province === "string" &&
      data.province.trim() !== ""
    );
  };

  const canApplyDiscount =
    isShippingInfoComplete(formData) &&
    (!hasCumiIntegration || !!selectedCumiFareyeCode) &&
    (isMarketplace
      ? getMarketplaceMissingShippingBrands().length === 0
      : !enforceShipping || !!selectedShippingOption);

  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof ShippingFormData, string>> = {};

    if (!formData.first_name.trim()) {
      errors.first_name = "First name is required";
    }
    if (!formData.last_name.trim()) {
      errors.last_name = "Last name is required";
    }
    if (!formData.phone.trim()) {
      errors.phone = "Phone number is required";
    } else if (!validateSAPhone(formData.phone)) {
      errors.phone = "Enter a valid South African phone number";
    }
    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Enter a valid email address";
    }
    if (!formData.address.trim()) {
      errors.address = "Street address is required";
    }
    if (!formData.suburb?.trim()) {
      errors.suburb = "Suburb is required";
    } else if (hasCumiIntegration && !selectedCumiFareyeCode) {
      errors.suburb = "Select a location for this suburb";
    }
    if (
      (!hasCumiIntegration || !!selectedCumiFareyeCode) &&
      !formData.city.trim()
    ) {
      errors.city = "City is required";
    }
    if (
      (!hasCumiIntegration || !!selectedCumiFareyeCode) &&
      !formData.postal_code.trim()
    ) {
      errors.postal_code = "Postal code is required";
    } else if (
      formData.postal_code &&
      !validateSAPostalCode(formData.postal_code)
    ) {
      errors.postal_code = "Enter a valid 4-digit postal code";
    }
    if (
      (!hasCumiIntegration || !!selectedCumiFareyeCode) &&
      !formData.province
    ) {
      errors.province = "Province is required";
    }
    if (enforceShipping && !isMarketplace && !formData.shipping_option_id) {
      errors.shipping_option_id = "Select a shipping method";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // -------------------------------------------------------------------------
  //#endregion
  // -------------------------------------------------------------------------

  // -------------------------------------------------------------------------
  //#region Local sub-components for marketplace shipping
  // -------------------------------------------------------------------------

  function MarketplaceShippingCards({
    brandCount,
    cartItemsByBrand,
    brandShippingMap,
    marketplaceRetailers,
    enforceShipping,
    onOptionChange,
    onStoreSelect,
    onCitySelect,
    onFindNearbyStores,
    onPickupCityChange,
  }: {
    brandCount: number;
    cartItemsByBrand: Map<string, CartItemType[]>;
    brandShippingMap: Map<string, BrandShippingState>;
    marketplaceRetailers: MarketplaceRetailer[];
    enforceShipping: boolean;
    onOptionChange: (
      brandId: string,
      option: {
        id: string;
        name: string;
        description: string;
        price_cents: number;
        carrier_code?: string;
        method_code?: string;
      },
    ) => void;
    onStoreSelect: (brandId: string, store: AmastyStoreLocation) => void;
    onCitySelect: (brandId: string, city: GeocodingResult) => void;
    onFindNearbyStores: (brandId: string) => void;
    onPickupCityChange: (brandId: string, value: string) => void;
  }) {
    return (
      <div className="space-y-4">
        {brandCount > 1 && (
          <p className="text-[14px] text-(--color-text-muted)">
            Items are shipped from different brands. Select a shipping method for each brand below.
          </p>
        )}
        {Array.from(cartItemsByBrand.entries()).map(([brandId, items]) => {
          const brandName =
            marketplaceRetailers.find((r) => r.retailer_id === brandId)?.name ||
            items[0]?.fulfillment_brand_name ||
            "Brand";
          const retailer = marketplaceRetailers.find((r) => r.retailer_id === brandId);
          const brandState = brandShippingMap.get(brandId) ?? initialBrandShippingState;
          const brandEnforce = enforceShipping || (retailer?.enforce_shipping ?? false);
          const availableMethods = brandState.availableMethods;

          return (
            <Card key={brandId} className="mb-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-[16px] font-semibold text-(--color-text-primary)">
                  {brandName}
                </h3>
                {brandEnforce && <span className="text-(--color-error)">*</span>}
              </div>

              <div className="mb-4 space-y-2">
                {items.map((item) => (
                  <div key={item.id} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md overflow-hidden border border-(--color-surface-border-light) shrink-0 bg-(--color-surface-secondary)">
                      {item.variant_image || item.image_url ? (
                        <img
                          src={item.variant_image || item.image_url || ""}
                          alt={item.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <svg
                            className="w-4 h-4 text-(--color-text-placeholder)"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] text-(--color-text-primary) truncate">{item.title}</p>
                      {item.selected_variant && (
                        <p className="text-[11px] text-(--color-text-muted)">
                          Variant: {item.selected_variant}
                        </p>
                      )}
                      <p className="text-[11px] text-(--color-text-muted)">
                        Qty: {item.quantity}
                      </p>
                    </div>
                    <div className="text-[13px] font-medium text-(--color-text-primary) tabular-nums">
                      {formatCurrency(item.line_total_cents)}
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-(--color-surface-border-light) pt-4">
                <h4 className="text-[14px] font-semibold text-(--color-text-primary) mb-3">
                  Shipping method {brandEnforce ? "(required)" : "(optional)"}
                </h4>

                {!formData.province ? (
                  <p className="text-[14px] text-(--color-text-muted)">
                    Select a province to see available shipping methods.
                  </p>
                ) : brandState.loading ? (
                  <p className="text-[14px] text-(--color-text-muted)">
                    Calculating shipping costs...
                  </p>
                ) : availableMethods.length === 0 ? (
                  <p className="text-[14px] text-(--color-text-muted)">
                    No shipping options available for this brand.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {availableMethods.map((option, idx) => {
                      const optionId = option.id || option.method_code || `shipping-${idx}`;
                      const selected = brandState.selectedOptionId === optionId;
                      return (
                        <label
                          key={optionId}
                          className={cn("shipping-option", selected && "selected")}
                        >
                          <div className="shipping-option-radio" />
                          <input
                            type="radio"
                            name={`shipping_option_${brandId}`}
                            value={optionId}
                            checked={selected}
                            onChange={() =>
                              onOptionChange(brandId, {
                                id: optionId,
                                name: option.name,
                                description: option.description,
                                price_cents: option.price_cents!,
                                carrier_code: option.carrier_code,
                                method_code: option.method_code,
                              })
                            }
                            className="sr-only"
                          />
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <span className="text-[14px] font-medium text-(--color-text-primary)">
                                {option.name}
                              </span>
                              <span className="text-[14px] font-medium text-(--color-text-primary) tabular-nums">
                                {formatCurrency(option.price_cents!)}
                              </span>
                            </div>
                            <p className="text-[12px] text-(--color-text-muted) mt-0.5">
                              {option.description}
                            </p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}

                {brandState.error && (
                  <p className="mt-2 text-[12px] text-(--color-error)">{brandState.error}</p>
                )}

                {selectedBrandIsAmasty(brandState) && retailer?.base_url && (
                  <Card className="mb-4 mt-4">
                    <h2 className="text-[16px] font-semibold text-(--color-text-primary) mb-4">
                      Find a Store
                    </h2>
                    <div className="space-y-4">
                      <div className="flex gap-3">
                        <div className="flex-1">
                          <Input
                            label="City or Suburb (Optional)"
                            value={brandState.pickupCity}
                            onChange={(e) => onPickupCityChange(brandId, e.target.value)}
                            placeholder="e.g Sandton"
                          />
                        </div>
                        <div className="flex items-end">
                          <Button
                            type="button"
                            onClick={() => onFindNearbyStores(brandId)}
                            isLoading={brandState.loadingPickupStores}
                            disabled={brandState.loadingPickupStores || brandState.showPickupCitySelection}
                          >
                            Find nearby stores
                          </Button>
                        </div>
                      </div>

                      <p className="text-[12px] text-(--color-text-secondary)">
                        On desktop, location may use your IP address and be inaccurate. Enter your city for better results.
                      </p>

                      {brandState.showPickupCitySelection && brandState.pickupCityCandidates.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-(--color-surface-border-light)">
                          <h3 className="text-[14px] font-medium text-(--color-text-primary) mb-3">
                            Select a location
                          </h3>
                          <div className="space-y-2 max-h-60 overflow-y-auto">
                            {brandState.pickupCityCandidates.map((city) => (
                              <button
                                key={city.lat + city.lon}
                                onClick={() => onCitySelect(brandId, city)}
                                className="w-full p-3 rounded-lg border border-(--color-surface-border-light) hover:border-(--color-brand-primary) hover:bg-(--color-brand-primary)/5 transition-all text-left"
                              >
                                <div className="text-[14px] font-medium text-(--color-text-primary)">
                                  {city.display_name}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {brandState.pickupStores.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-(--color-surface-border-light)">
                          <h3 className="text-[14px] font-medium text-(--color-text-primary) mb-3">
                            Select a pickup location
                          </h3>
                          <div className="space-y-2 max-h-60 overflow-y-auto">
                            {brandState.pickupStores.map((store) => (
                              <label
                                key={store.id}
                                className={cn(
                                  "shipping-option",
                                  brandState.pickupStore === store.id && "selected",
                                )}
                              >
                                <div className="shipping-option-radio" />
                                <input
                                  type="radio"
                                  name={`amasty_store_${brandId}`}
                                  value={store.id}
                                  checked={brandState.pickupStore === store.id}
                                  onChange={() => onStoreSelect(brandId, store)}
                                  className="sr-only"
                                />
                                <div className="flex-1">
                                  <div className="flex items-center justify-between">
                                    <span className="text-[14px] font-medium text-(--color-text-primary)">
                                      {store.name}
                                    </span>
                                    {store.distance !== undefined && (
                                      <span className="text-[12px] text-(--color-text-muted)">
                                        {store.distance.toFixed(1)} km
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-[12px] text-(--color-text-muted) mt-0.5">
                                    {store.address}
                                  </p>
                                  {store.working_time_today && (
                                    <p className="text-[12px] text-(--color-text-muted) mt-0.5">
                                      Open today: {store.working_time_today}
                                    </p>
                                  )}
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      {brandState.pickupError && (
                        <p className="text-[14px] text-(--color-text-muted)">{brandState.pickupError}</p>
                      )}
                    </div>
                  </Card>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    );
  }

  function selectedBrandIsAmasty(brandState: BrandShippingState): boolean {
    const selected = brandState.availableMethods.find(
      (option) =>
        option.id === brandState.selectedOptionId ||
        option.method_code === brandState.selectedOptionId ||
        `shipping-${brandState.availableMethods.indexOf(option)}` === brandState.selectedOptionId,
    );
    return (
      selected?.name?.toLowerCase().includes("amstorepickup") ||
      selected?.carrier_code?.toLowerCase().includes("amstorepickup") ||
      false
    );
  }

  // -------------------------------------------------------------------------
  //#endregion
  // -------------------------------------------------------------------------

  // -------------------------------------------------------------------------
  //#region Render
  // -------------------------------------------------------------------------

  // Loading state
  if (cartLoading || (isMarketplace && marketplaceSessionLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-(--color-bg-primary)">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Error state
  if (cartError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-(--color-bg-primary)">
        <ErrorMessage
          title="Failed to load cart"
          message={
            cartError instanceof Error ? cartError.message : "An error occurred"
          }
          className="max-w-md"
        />
      </div>
    );
  }

  // Empty cart
  if (!cart || cart.items.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-(--color-bg-primary)">
        <Card className="max-w-sm w-full text-center">
          <div className="empty-state py-4">
            <svg
              className="empty-state-icon"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
              />
            </svg>
            <h2 className="text-[18px] font-semibold text-(--color-text-primary)">
              Your cart is empty
            </h2>
            <p className="text-[14px] text-(--color-text-muted) mt-2">
              No items in your cart at the moment.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-(--color-bg-primary) flex flex-col justify-between gap-5">
      <div>
        {/* Header */}
        <header className="bg-(--color-surface-primary) border-b border-(--color-surface-border-light)">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between flex-wrap-reverse">
              <BrandLogo brand={theme} />
              <SecureCheckoutBadge />
            </div>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-4 py-8">
          <CheckoutBreadcrumb currentStep={step} />
          <div className="lg:grid lg:grid-cols-[1fr_380px] lg:gap-4">
            {/* Main content */}
            <div>
              {step === "shipping" ? (
                <>
                  {/* Shipping section */}
                  <Card className="mb-4">
                    <p className="text-(--color-text-primary) text-sm">
                      All fields with an asterisk{" "}
                      <span className="text-(--color-error)">*</span> are
                      required
                    </p>
                  </Card>

                  <Card className="mb-4">
                    <h2 className="text-[16px] font-semibold text-(--color-text-primary) mb-4">
                      Contact information
                    </h2>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Input
                          label="First name"
                          value={formData.first_name}
                          onChange={(e) =>
                            handleInputChange("first_name", e.target.value)
                          }
                          error={formErrors.first_name}
                          placeholder="e.g John"
                          required={true}
                        />
                        <Input
                          label="Last name"
                          value={formData.last_name}
                          onChange={(e) =>
                            handleInputChange("last_name", e.target.value)
                          }
                          error={formErrors.last_name}
                          placeholder="e.g Doe"
                          required={true}
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Input
                          label="Phone number"
                          value={formData.phone}
                          onChange={(e) =>
                            handleInputChange("phone", e.target.value)
                          }
                          onBlur={handlePhoneBlur}
                          error={formErrors.phone}
                          placeholder="e.g 082 123 4567"
                          type="tel"
                          required={true}
                        />
                        <Input
                          label="Email"
                          value={formData.email}
                          onChange={(e) =>
                            handleInputChange("email", e.target.value)
                          }
                          error={formErrors.email}
                          placeholder="e.g john@example.com"
                          type="email"
                          required={true}
                        />
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-(--color-surface-border-light)">
                      <Checkbox
                        label="Update contact details"
                        checked={updateContactDetails}
                        onChange={(e) =>
                          setUpdateContactDetails(e.target.checked)
                        }
                      />
                    </div>
                  </Card>

                  {/* Shipping address */}
                  <Card className="mb-4">
                    <h2 className="text-[16px] font-semibold text-(--color-text-primary) mb-4">
                      Shipping address
                    </h2>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Input
                          label="Street address"
                          value={formData.address}
                          onChange={(e) =>
                            handleInputChange("address", e.target.value)
                          }
                          error={formErrors.address}
                          placeholder="e.g 123 Main Street"
                          required={true}
                        />
                        <Input
                          label="Apartment/Unit number"
                          value={formData.address2}
                          onChange={(e) =>
                            handleInputChange("address2", e.target.value)
                          }
                          error={formErrors.address2}
                          placeholder="e.g Unit 4"
                          required={false}
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {hasCumiIntegration ? (
                          <CumiSuburbLookup
                            value={formData.suburb}
                            selectedCode={selectedCumiFareyeCode}
                            error={formErrors.suburb}
                            onSuburbChange={handleCumiSuburbChange}
                            onLocationSelect={handleCumiLocationSelect}
                          />
                        ) : (
                          <Input
                            label="Suburb"
                            value={formData.suburb}
                            onChange={(e) =>
                              handleInputChange("suburb", e.target.value)
                            }
                            error={formErrors.suburb}
                            placeholder="e.g Benoni"
                            required={true}
                          />
                        )}
                        <Input
                          label="City"
                          value={formData.city}
                          onChange={(e) =>
                            handleInputChange("city", e.target.value)
                          }
                          error={formErrors.city}
                          placeholder="e.g Johannesburg"
                          required={true}
                          disabled={hasCumiIntegration}
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Input
                          label="Postal code"
                          value={formData.postal_code}
                          onChange={(e) =>
                            handleInputChange("postal_code", e.target.value)
                          }
                          error={formErrors.postal_code}
                          placeholder="e.g 2000"
                          required={true}
                          disabled={hasCumiIntegration}
                        />
                        <Select
                          label="Province"
                          value={formData.province}
                          onChange={(e) =>
                            handleInputChange("province", e.target.value)
                          }
                          error={formErrors.province}
                          required={true}
                          disabled={hasCumiIntegration}
                        >
                          <option value="">Select province</option>
                          {SA_PROVINCES.map((province) => (
                            <option key={province} value={province}>
                              {province}
                            </option>
                          ))}
                        </Select>
                      </div>
                    </div>
                    {isFormPrefilled && (
                      <div className="mt-4 pt-4 border-t border-(--color-surface-border-light)">
                        <Checkbox
                          label="Update shipping details"
                          checked={updateShippingDetails}
                          onChange={(e) =>
                            setUpdateShippingDetails(e.target.checked)
                          }
                        />
                      </div>
                    )}
                  </Card>

                  {isMarketplace ? (
                    <MarketplaceShippingCards
                      brandCount={brandCount}
                      cartItemsByBrand={cartItemsByBrand}
                      brandShippingMap={brandShippingMap}
                      marketplaceRetailers={marketplaceRetailers}
                      enforceShipping={enforceShipping}
                      onOptionChange={handleBrandShippingOptionChange}
                      onStoreSelect={handleBrandAmastyStoreSelect}
                      onCitySelect={handleBrandCitySelect}
                      onFindNearbyStores={handleBrandFindNearbyStores}
                      onPickupCityChange={(brandId, value) =>
                        updateBrandShipping(brandId, { pickupCity: value })
                      }
                    />
                  ) : (
                    <Card className="mb-4">
                      <h2 className="text-[16px] font-semibold text-(--color-text-primary) mb-4">
                        Shipping method{" "}
                        {enforceShipping ? (
                          <span className="text-(--color-error) ml-0.5">*</span>
                        ) : (
                          "(optional)"
                        )}
                      </h2>
                      {!formData.province ? (
                        <p className="text-[14px] text-(--color-text-muted)">
                          Select a province to see available shipping methods for
                          that province
                        </p>
                      ) : calculatingShipping ? (
                        <p className="text-[14px] text-(--color-text-muted)">
                          Calculating shipping costs...
                        </p>
                      ) : Array.isArray(shippingData) ? (
                        (() => {
                          const availableMethodsWithIndex = availableShippingMethods
                            .map((option, originalIdx) => ({
                              option,
                              originalIdx,
                            }));

                          if (availableMethodsWithIndex.length === 0) {
                            return (
                              <p className="text-[14px] text-(--color-text-muted)">
                                No shipping options available for the selected
                                province and cart value
                              </p>
                            );
                          }

                          return (
                            <div className="space-y-3">
                              {availableMethodsWithIndex.map(
                                ({ option, originalIdx }) => {
                                  const optionId =
                                    option.id ||
                                    option.method_code ||
                                    `shipping-${originalIdx}`;
                                  return (
                                    <label
                                      key={optionId}
                                      className={cn(
                                        "shipping-option",
                                        formData.shipping_option_id === optionId &&
                                          "selected",
                                      )}
                                    >
                                      <div className="shipping-option-radio" />
                                      <input
                                        type="radio"
                                        name="shipping_option"
                                        value={optionId}
                                        checked={
                                          formData.shipping_option_id === optionId
                                        }
                                        onChange={() =>
                                          handleShippingOptionChange({
                                            id: optionId,
                                            name: option.name,
                                            description: option.description,
                                            price_cents: option.price_cents!,
                                            type: option.type,
                                            carrier_code: option.carrier_code,
                                            method_code: option.method_code,
                                          })
                                        }
                                        className="sr-only"
                                      />
                                      <div className="flex-1">
                                        <div className="flex items-center justify-between">
                                          <span className="text-[14px] font-medium text-(--color-text-primary)">
                                            {option.name}
                                          </span>
                                          <span className="text-[14px] font-medium text-(--color-text-primary) tabular-nums">
                                            {formatCurrency(option.price_cents!)}
                                          </span>
                                        </div>
                                        <p className="text-[12px] text-(--color-text-muted) mt-0.5">
                                          {option.description}
                                        </p>
                                      </div>
                                    </label>
                                  );
                                },
                              )}
                            </div>
                          );
                        })()
                      ) : (
                        <p className="text-[14px] text-(--color-text-muted)">
                          No shipping options available
                        </p>
                      )}
                      {formErrors.shipping_option_id && (
                        <p className="mt-2 text-[12px] text-(--color-error)">
                          {formErrors.shipping_option_id}
                        </p>
                      )}

                      {selectedShippingOption &&
                        (isStorePickupOption ||
                          selectedShippingOption.carrier_code
                            ?.toLowerCase()
                            .includes("amstorepickup") ||
                          selectedShippingOption.name
                            ?.toLowerCase()
                            .includes("amstorepickup")) && (
                        <Card className="mb-4 mt-4">
                          <h2 className="text-[16px] font-semibold text-(--color-text-primary) mb-4">
                            {isStorePickupOption
                              ? "Store Pickup Options"
                              : "Find a Store"}
                          </h2>
                          <div className="space-y-4">
                            {!isStorePickupOption && (
                              <div className="flex gap-3">
                                <div className="flex-1">
                                  <Input
                                    label="City or Suburb (Optional)"
                                    value={pickupCity}
                                    onChange={(e) => {
                                      setPickupCity(e.target.value);
                                    }}
                                    placeholder="e.g Sandton"
                                  />
                                </div>
                                <div className="flex items-end">
                                  <Button
                                    type="button"
                                    onClick={handleFindNearbyStores}
                                    isLoading={loadingPickupLocations}
                                    disabled={
                                      loadingPickupLocations ||
                                      showCitySelection
                                    }
                                  >
                                    Find nearby stores
                                  </Button>
                                </div>
                              </div>
                            )}

                            {!isStorePickupOption && (
                              <p className="text-[12px] text-(--color-text-secondary)">
                                On desktop, location may use your IP address and be inaccurate. Enter your city for better results.
                              </p>
                            )}

                            {!isStorePickupOption &&
                              showCitySelection &&
                              cityCandidates.length > 0 && (
                                <div className="mt-4 pt-4 border-t border-(--color-surface-border-light)">
                                  <h3 className="text-[14px] font-medium text-(--color-text-primary) mb-3">
                                    Select a location
                                  </h3>
                                  <div className="space-y-2 max-h-60 overflow-y-auto">
                                    {cityCandidates.map((city) => (
                                      <button
                                        key={city.lat + city.lon}
                                        onClick={() =>
                                          handleCitySelect(city)
                                        }
                                        className="w-full p-3 rounded-lg border border-(--color-surface-border-light) hover:border-(--color-brand-primary) hover:bg-(--color-brand-primary)/5 transition-all text-left"
                                      >
                                        <div className="text-[14px] font-medium text-(--color-text-primary)">
                                          {city.display_name}
                                        </div>
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}

                            {isStorePickupOption &&
                              loadingPickupLocations && (
                                <div className="flex items-center gap-2 text-[14px] text-(--color-text-muted)">
                                  <LoadingSpinner size="sm" />
                                  <span>Loading pickup locations...</span>
                                </div>
                              )}

                            {pickupLocations.length > 0 && (
                              <div className="mt-4 pt-4 border-t border-(--color-surface-border-light)">
                                <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                  <h3 className="text-[14px] font-medium text-(--color-text-primary)">
                                    Select a pickup location
                                  </h3>
                                  {isStorePickupOption && (
                                    <div className="w-full sm:max-w-xs">
                                      <Input
                                        id="pickup-location-search"
                                        type="search"
                                        aria-label="Search pickup locations"
                                        value={pickupLocationSearch}
                                        onChange={(event) =>
                                          setPickupLocationSearch(
                                            event.target.value,
                                          )
                                        }
                                        placeholder="Search name or address"
                                      />
                                    </div>
                                  )}
                                </div>
                                <div className="space-y-2 max-h-60 overflow-y-auto">
                                  {visiblePickupLocations.map((location:any) => (
                                    <label
                                      key={location.id}
                                      className={cn(
                                        "shipping-option",
                                        selectedPickupLocationId === location.id &&
                                          "selected",
                                      )}
                                    >
                                      <div className="shipping-option-radio" />
                                      <input
                                        type="radio"
                                        name="pickup_location"
                                        value={location.id}
                                        checked={
                                          selectedPickupLocationId === location.id
                                        }
                                        onChange={() =>
                                          handlePickupLocationSelect(location)
                                        }
                                        className="sr-only"
                                      />
                                      <div className="flex-1">
                                        <div className="flex items-center justify-between">
                                          <span className="text-[14px] font-medium text-(--color-text-primary)">
                                            {location.name}
                                          </span>
                                          {location.distance !== undefined && (
                                            <span className="text-[12px] text-(--color-text-muted)">
                                              {location.distance.toFixed(1)} km
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-[12px] text-(--color-text-muted) mt-0.5">
                                          {location.address}
                                        </p>
                                        {location.working_time_today && (
                                          <p className="text-[12px] text-(--color-text-muted) mt-0.5">
                                            Open today:{" "}
                                            {location.working_time_today}
                                          </p>
                                        )}
                                      </div>
                                    </label>
                                  ))}
                                  {isStorePickupOption &&
                                    visiblePickupLocations.length === 0 && (
                                      <p className="py-3 text-[13px] text-(--color-text-muted)">
                                        No pickup locations match your search.
                                      </p>
                                    )}
                                </div>
                              </div>
                            )}

                            {pickupLocationsError && (
                              <p className="text-[14px] text-(--color-text-muted)">
                                {pickupLocationsError}
                              </p>
                            )}
                          </div>
                        </Card>
                      )}
                    </Card>
                  )}

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      onClick={handleBackToCart}
                      variant="accent"
                      className="sm:order-1 opacity-100"
                    >
                      Back to cart
                    </Button>
                    <Button
                      onClick={handleProceedToReview}
                      fullWidth
                      size="lg"
                      className="sm:order-2"
                    >
                      Continue to review
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  {/* Review section */}
                  <Card className="mb-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h2 className="text-[16px] font-semibold text-(--color-text-primary)">
                          Review your order
                        </h2>
                        <p className="text-[13px] text-(--color-text-muted) mt-1">
                          Confirm your items and delivery details before
                          continuing to payment.
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleBackToShipping}
                      >
                        Edit
                      </Button>
                    </div>
                  </Card>

                  <Card className="mb-4">
                    <h3 className="text-[16px] font-semibold text-(--color-text-primary) mb-4">
                      Items
                    </h3>
                    <div>
                      {cart.items.map((item) => (
                        <CartItem
                          key={item.id}
                          item={item}
                          isMarketplace={isMarketplace}
                          isFreeItem={getDiscountForCartItem(item)}
                          compact
                        />
                      ))}
                    </div>
                  </Card>

                  <Card className="mb-4">
                    <h3 className="text-[16px] font-semibold text-(--color-text-primary) mb-4">
                      Delivery {!enforceShipping && !isMarketplace && "(Optional)"}
                    </h3>
                    <div className="space-y-2 text-[14px]">
                      <div className="flex justify-between gap-4">
                        <span className="text-(--color-text-secondary)">
                          Recipient
                        </span>
                        <span className="text-(--color-text-primary) font-medium text-right">
                          {formData.first_name || "—"}{" "}
                          {formData.last_name || "—"}
                        </span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-(--color-text-secondary)">
                          Phone
                        </span>
                        <span className="text-(--color-text-primary) font-medium tabular-nums text-right">
                          {formData.phone || "—"}
                        </span>
                      </div>
                      {formData.email && (
                        <div className="flex justify-between gap-4">
                          <span className="text-(--color-text-secondary)">
                            Email
                          </span>
                          <span className="text-(--color-text-primary) font-medium text-right">
                            {formData.email}
                          </span>
                        </div>
                      )}
                      <div className="divider" />
                      <div className="flex justify-between gap-4">
                        <span className="text-(--color-text-secondary)">
                          Address
                        </span>
                        <span className="text-(--color-text-primary) font-medium text-right">
                          {formData.address ||
                          formData.address2 ||
                          formData.suburb ||
                          formData.city ||
                          formData.province ||
                          formData.postal_code
                            ? [
                                formData.address,
                                formData.address2,
                                formData.suburb,
                                formData.city,
                                [formData.province, formData.postal_code]
                                  .filter(Boolean)
                                  .join(" "),
                              ]
                                .filter(Boolean)
                                .join(", ")
                            : "—"}
                        </span>
                      </div>
                      {isMarketplace ? (
                        <div className="space-y-3">
                          {Array.from(cartItemsByBrand.entries()).map(([brandId, items]) => {
                            const selected = getMarketplaceSelectedMethod(brandId);
                            const brandState = brandShippingMap.get(brandId) ?? initialBrandShippingState;
                            const pickupStore = brandState.pickupStore
                              ? brandState.pickupStores.find((s) => s.id === brandState.pickupStore)
                              : null;
                            return (
                              <div key={brandId} className="pt-3 border-t border-(--color-surface-border-light)">
                                <p className="text-[13px] font-medium text-(--color-text-primary) mb-1">
                                  {getBrandName(brandId)}
                                </p>
                                <div className="flex justify-between gap-4">
                                  <span className="text-(--color-text-secondary)">
                                    Shipping method
                                  </span>
                                  <span className="text-(--color-text-primary) font-medium text-right">
                                    {selected
                                      ? selected.method_code?.toLowerCase().includes("amstorepickup")
                                        ? `${selected.name} - ${pickupStore?.name} (${formatCurrency(selected.price_cents!)})`
                                        : `${selected.name} (${formatCurrency(selected.price_cents!)})`
                                      : "—"}
                                  </span>
                                </div>
                                <div className="mt-2 space-y-1">
                                  {items.map((item) => (
                                    <div key={item.id} className="flex items-center gap-2 text-[12px] text-(--color-text-muted)">
                                      <span className="truncate">{item.title}</span>
                                      <span className="tabular-nums">x{item.quantity}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="flex justify-between gap-4">
                          <span className="text-(--color-text-secondary)">
                            Shipping method
                          </span>
                          <span className="text-(--color-text-primary) font-medium text-right">
                            {selectedShippingOption
                              ? isStorePickupOption ||
                                selectedShippingOption.method_code
                                  ?.toLowerCase()
                                  .includes("amstorepickup")
                                ? `${selectedShippingOption.name} - ${pickupLocations.find((location) => location.id === selectedPickupLocationId)?.name} (${formatCurrency(selectedShippingOption.price_cents!)})`
                                : `${selectedShippingOption.name} (${formatCurrency(selectedShippingOption.price_cents!)})`
                              : enforceShipping
                                ? "—"
                                : "Not selected (optional)"}
                          </span>
                        </div>
                      )}
                    </div>
                  </Card>

                  <Card className="mb-4">
                    <h3 className="text-[16px] font-semibold text-(--color-text-primary) mb-4">
                      Totals
                    </h3>
                    <div className="space-y-2 text-[14px]">
                      <div className="flex justify-between">
                        <span className="text-(--color-text-secondary)">
                          Subtotal
                        </span>
                        <span className="text-(--color-text-primary) font-medium tabular-nums">
                          {formatCurrency(cart.subtotal_cents)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-(--color-text-secondary)">
                          VAT (15%)
                        </span>
                        <span className="text-(--color-text-primary) font-medium tabular-nums">
                          {formatCurrency(vatCents)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-(--color-text-secondary)">
                          Shipping
                        </span>
                        <span className="text-(--color-text-primary) font-medium tabular-nums">
                          {isMarketplace || selectedShippingOption
                            ? formatCurrency(shippingCents)
                            : "—"}
                        </span>
                      </div>
                      <div className="divider" />
                      <div className="flex justify-between items-baseline">
                        <span className="text-(--color-text-primary) font-semibold">
                          Total
                        </span>
                        <span className="price-total tabular-nums">
                          {formatCurrency(
                            totalCents - totalAppliedDiscount - automatedDiscountCents,
                          )}
                        </span>
                      </div>
                    </div>
                  </Card>

                  {orderError && (
                    <div className="error-box mb-4">
                      <svg
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                      <div>
                        <p className="font-medium">Failed to create order</p>
                        <p className="text-[12px] opacity-80 mt-0.5">
                          {orderError}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      onClick={handleBackToShipping}
                      variant="accent"
                      className="sm:order-1 opacity-100"
                    >
                      Back to shipping
                    </Button>
                    <Button
                      onClick={() => HandleSubmitOrder()}
                      disabled={
                        enforceShipping && !isMarketplace && !formData.shipping_option_id
                      }
                      isLoading={isPrefilPending || discountRecalculating || createDraftOrder.isPending}
                      fullWidth
                      size="lg"
                      className="sm:order-2"
                    >
                      Continue to payment
                    </Button>
                  </div>
                </>
              )}
            </div>

            {/* Order summary sidebar */}
            <div className="hidden lg:block">
              <div className="sticky top-8">
                <Card>
                  <h2 className="text-[16px] font-semibold text-(--color-text-primary) mb-4">
                    Order summary
                  </h2>

                  {/* Products */}
                  <div className="border-b border-(--color-surface-border-light) pb-4 mb-4">
                    {cart.items.map((item) => (
                      <CartItem
                        key={item.id}
                        item={item}
                        isMarketplace={isMarketplace}
                        isFreeItem={getDiscountForCartItem(item)}
                        compact
                      />
                    ))}
                  </div>

                  {/* Totals */}
                  <div className="space-y-2 text-[14px]">
                    <div className="flex justify-between">
                      <span className="text-(--color-text-secondary)">
                        Subtotal
                      </span>
                      <span className="text-(--color-text-primary) font-medium tabular-nums">
                        {formatCurrency(cart.subtotal_cents)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-(--color-text-secondary)">
                        VAT (15%)
                      </span>
                      <span className="text-(--color-text-primary) font-medium tabular-nums">
                        {formatCurrency(vatCents)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-(--color-text-secondary)">
                        Shipping
                      </span>
                      <span className="text-(--color-text-primary) font-medium tabular-nums">
                        {isMarketplace
                          ? formatCurrency(shippingCents)
                          : selectedShippingOption
                            ? formatCurrency(shippingCents)
                            : "—"}
                      </span>
                    </div>
                    {isMarketplace ? (
                        <div className="space-y-1 -mt-1">
                          {Array.from(cartItemsByBrand.keys()).map((brandId) => {
                            const selected = getMarketplaceSelectedMethod(brandId);
                            const brandState = brandShippingMap.get(brandId) ?? initialBrandShippingState;
                            const pickupStore = brandState.pickupStore
                              ? brandState.pickupStores.find((s) => s.id === brandState.pickupStore)
                              : null;
                            return selected ? (
                              <div key={brandId} className="flex justify-between">
                                <p className="text-[12px] text-(--color-text-muted)">
                                  {getBrandName(brandId)}
                                </p>
                                <p className="text-[12px] text-(--color-text-muted) text-right">
                                  {selected.method_code?.toLowerCase().includes("amstorepickup")
                                    ? `${selected.name} - ${pickupStore?.name}`
                                    : selected.name}
                                </p>
                              </div>
                            ) : null;
                          })}
                        </div>
                      ) : selectedShippingOption ? (
                        <div className="flex justify-between -mt-1">
                          <p className="text-[12px] text-(--color-text-muted)">
                            {selectedShippingOption.name}
                          </p>
                          {selectedPickupLocationId && (
                            <p className="text-[12px] text-(--color-text-muted) text-right">
                              {
                                pickupLocations.find(
                                  (location) => location.id === selectedPickupLocationId,
                                )?.name
                              }
                            </p>
                          )}
                        </div>
                      ) : null
                    }
                    <div className="mt-3">
                      <div className="flex items-center gap-2 sm:gap-3">
                          <input
                            type="text"
                            value={discountCode}
                            onChange={(e) => {
                              setDiscountCode(e.target.value);
                              setDiscountError(null);
                              setDiscountWarning(null);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                ConfirmAndUseDiscount(totalCents);
                              }
                            }}
                            placeholder={canApplyDiscount ? "Discount code" : "Fill in shipping details first"}
                            disabled={!!discountPercentage || !canApplyDiscount}
                            className={cn(
                              "flex-1 min-w-0 h-11 px-3 sm:px-4 text-[13px] sm:text-[14px] bg-(--color-surface-primary) border rounded-lg text-(--color-text-primary) placeholder:text-(--color-text-placeholder) transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-0 disabled:bg-(--color-bg-secondary)bled:text-(--color-text-muted) disabled:cursor-not-allowed",
                              discountPercentage
                                ? "border-(--color-success) focus:ring-(--color-success)/20 focus:border-(--color-success)"
                                : discountError
                                  ? "border-(--color-error) focus:ring-(--color-error)/20 focus:border-(--color-error)"
                                  : "border-(--color-surface-border) focus:ring-(--color-primary)/20 focus:border-(--color-primary)",
                            )}
                          />
                          {!discountPercentage && (
                            <button
                              type="button"
                              onClick={() =>
                                ConfirmAndUseDiscount(totalCents)
                              }
                              disabled={validateDiscount.isPending || discountRecalculating || !canApplyDiscount}
                              className={cn(
                                "shrink-0 inline-flex items-center justify-center min-h-11 min-w-11 w-11 sm:w-auto sm:h-10 sm:px-4 sm:gap-1.5 rounded-lg text-(--color-secondary) text-[13px] sm:text-[14px] font-medium transition-colors",
                                validateDiscount.isPending || discountRecalculating || !canApplyDiscount
                                  ? "bg-(--color-primary) opacity-70 cursor-wait"
                                  : "bg-(--color-primary) hover:bg-(--color-primary-hover)",
                              )}
                              aria-label="Apply discount code"
                            >
                              {validateDiscount.isPending || discountRecalculating ? (
                                <svg
                                  className="w-4 h-4 animate-spin"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V4C5.373 4 2.525 7.037 2.005 10.82A.967.967 0 002 12h2z"
                                  />
                                </svg>
                              ) : (
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 12h14M12 5l7 7-7 7"
                                  />
                                </svg>
                              )}
                            </button>
                          )}
                          {discountPercentage && (
                            <svg
                              className="w-5 h-5 text-(--color-success) shrink-0"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          )}
                      </div>
                      <div className="mt-3">
                      {totalDiscountDetail.length > 0
                        ? totalDiscountDetail.map((d, idx) => (
                            <div
                              key={`${d.code}-${d.brandId || idx}`}
                              className={cn(
                                "flex justify-between items-center py-1.5",
                                idx < totalDiscountDetail.length - 1 && "border-b border-(--color-surface-border-light)",
                              )}
                            >
                              <span className="text-(--color-text-secondary) truncate pr-2 flex items-center gap-1.5">
                                {d.code}{d.brandId && isMarketplace ? ` (${getBrandName(d.brandId)})` : ""}
                                <button
                                  type="button"
                                  onClick={() => handleRemoveDiscount(d.code, d.brandId)}
                                  className="text-(--color-error) hover:text-(--color-error)/80 transition-colors shrink-0"
                                  aria-label={`Remove ${d.code}`}
                                >
                                  <svg
                                    className="w-3.5 h-3.5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2.5}
                                      d="M6 18L18 6M6 6l12 12"
                                    />
                                  </svg>
                                </button>
                              </span>
                              <span className="text-(--color-success) font-medium tabular-nums shrink-0">
                                - {formatCurrency(d.totalValue)}
                              </span>
                            </div>
                          ))
                        : null
                      }
                      {automatedDiscountCents > 0 && (
                        <AutomatedDiscountRow amountCents={automatedDiscountCents} />
                      )}
                      </div>
                      {discountError && (
                        <p className="text-[12px] text-(--color-error) mt-1">
                          {discountError}
                        </p>
                      )}
                      {discountWarning && !discountError && (
                        <p className="text-[12px] text-amber-600 mt-1">
                          {discountWarning}
                        </p>
                      )}
                    </div>
                    <div className="divider" />
                    <div className="flex justify-between items-baseline">
                      <span className="text-(--color-text-primary) font-semibold">
                        Total
                      </span>
                      <span className="price-total tabular-nums">
                        {formatCurrency(totalCents - totalAppliedDiscount - automatedDiscountCents)}
                      </span>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>

          {/* Mobile order summary toggle */}
          <div ref={mobileBottomBarRef} className="lg:hidden fixed bottom-0 left-0 right-0 bg-(--color-surface-primary) border-t border-(--color-surface-border-light) p-4 z-50">
            {/* Discount code input — always visible above accordion on mobile */}
            <div className="mb-3">
              <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={discountCode}
                    onChange={(e) => {
                      setDiscountCode(e.target.value);
                      setDiscountError(null);
                      setDiscountWarning(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        ConfirmAndUseDiscount(totalCents);
                      }
                    }}
                    placeholder={canApplyDiscount ? "Discount code" : "Fill in shipping details first"}
                    disabled={!!discountPercentage || !canApplyDiscount}
                    className={cn(
                      "flex-1 min-w-0 h-11 px-3 text-[13px] bg-(--color-surface-primary) border rounded-lg text-(--color-text-primary) placeholder:text-(--color-text-placeholder) transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-0 disabled:bg-(--color-bg-secondary) disabled:text-(--color-text-muted) disabled:cursor-not-allowed",
                      discountPercentage
                        ? "border-(--color-success) focus:ring-(--color-success)/20 focus:border-(--color-success)"
                        : discountError
                          ? "border-(--color-error) focus:ring-(--color-error)/20 focus:border-(--color-error)"
                          : "border-(--color-surface-border) focus:ring-(--color-primary)/20 focus:border-(--color-primary)",
                    )}
                  />
                  {!discountPercentage && showSummary && (
                    <button
                      type="button"
                      onClick={() =>
                        ConfirmAndUseDiscount(totalCents)
                      }
                      disabled={validateDiscount.isPending || discountRecalculating || !canApplyDiscount}
                      className={cn(
                        "shrink-0 inline-flex items-center justify-center min-h-11 min-w-11 w-11 rounded-lg text-(--color-secondary) text-[13px] font-medium transition-colors",
                        validateDiscount.isPending || discountRecalculating || !canApplyDiscount
                          ? "bg-(--color-primary) opacity-70 cursor-wait"
                          : "bg-(--color-primary) hover:bg-(--color-primary-hover)",
                      )}
                      aria-label="Apply discount code"
                    >
                      {validateDiscount.isPending || discountRecalculating ? (
                        <svg
                          className="w-4 h-4 animate-spin"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V4C5.373 4 2.525 7.037 2.005 10.82A.967.967 0 002 12h2z"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 12h14M12 5l7 7-7 7"
                          />
                        </svg>
                      )}
                    </button>
                  )}
                  {discountPercentage && showSummary && (
                    <svg
                      className="w-5 h-5 text-(--color-success) shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
              </div>
              <div className="mt-3">
              {totalDiscountDetail.length > 0
                ? totalDiscountDetail.map((d, idx) => (
                    <div
                      key={`${d.code}-${d.brandId || idx}`}
                      className={cn(
                        "flex justify-between items-center text-[13px] py-1.5",
                        idx < totalDiscountDetail.length - 1 && "border-b border-(--color-surface-border-light)",
                      )}
                    >
                      <span className="text-(--color-text-secondary) truncate pr-2 flex items-center gap-1.5">
                        {d.code}{d.brandId && isMarketplace ? ` (${getBrandName(d.brandId)})` : ""}
                        <button
                          type="button"
                          onClick={() => handleRemoveDiscount(d.code, d.brandId)}
                          className="text-(--color-error) hover:text-(--color-error)/80 transition-colors shrink-0"
                          aria-label={`Remove ${d.code}`}
                        >
                          <svg
                            className="w-3.5 h-3.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2.5}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </span>
                      <span className="text-(--color-success) font-medium tabular-nums shrink-0">
                        - {formatCurrency(d.totalValue)}
                      </span>
                    </div>
                  ))
                : null
              }
              {automatedDiscountCents > 0 && (
                <AutomatedDiscountRow amountCents={automatedDiscountCents} />
              )}
              </div>
              {discountError && (
                <p className="text-[12px] text-(--color-error) mt-1">
                  {discountError}
                </p>
              )}
              {discountWarning && !discountError && (
                <p className="text-[12px] text-amber-600 mt-1">
                  {discountWarning}
                </p>
              )}
            </div>

            <div className="flex items-center justify-between mb-3">
              <button
                onClick={() => {
                  const opening = !showSummary;
                  setShowSummary(!showSummary);
                  if (opening) {
                    // After the spacer grows, scroll down so buttons stay visible
                    requestAnimationFrame(() => {
                      window.scrollBy({ top: mobileBottomBarRef.current ? mobileBottomBarRef.current.offsetHeight * 0.3 : 80, behavior: 'smooth' });
                    });
                  }
                }}
                className="flex items-center gap-2 text-[14px] text-(--color-secondary)"
              >
                <svg
                  className={cn(
                    "w-4 h-4 transition-transform",
                    showSummary && "rotate-180",
                  )}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
                {showSummary ? "Hide" : "Show"} order summary
              </button>
              <span className="price-total tabular-nums">
                {formatCurrency(totalCents - totalAppliedDiscount - automatedDiscountCents)}
              </span>
            </div>

            {showSummary && (
              <div className="mb-4 max-h-60 overflow-y-auto border-t border-(--color-surface-border-light) pt-4">
                {cart.items.map((item) => (
                  <CartItem
                    key={item.id}
                    item={item}
                    isMarketplace={isMarketplace}
                    isFreeItem={getDiscountForCartItem(item)}
                    compact
                  />
                ))}
                <div className="divider" />
                <div className="space-y-2 text-[13px]">
                  <div className="flex justify-between">
                    <span className="text-(--color-text-secondary)">
                      Subtotal
                    </span>
                    <span className="tabular-nums">
                      {formatCurrency(cart.subtotal_cents)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-(--color-text-secondary)">
                      VAT (15%)
                    </span>
                    <span className="tabular-nums">
                      {formatCurrency(vatCents)}
                    </span>
                  </div>
                  {isMarketplace ? (
                      <>
                        <div className="flex justify-between">
                          <span className="text-(--color-text-secondary)">
                            Shipping
                          </span>
                          <span className="tabular-nums">
                            {formatCurrency(shippingCents)}
                          </span>
                        </div>
                        <div className="space-y-1 -mt-1">
                          {Array.from(cartItemsByBrand.keys()).map((brandId) => {
                            const selected = getMarketplaceSelectedMethod(brandId);
                            const brandState = brandShippingMap.get(brandId) ?? initialBrandShippingState;
                            const pickupStore = brandState.pickupStore
                              ? brandState.pickupStores.find((s) => s.id === brandState.pickupStore)
                              : null;
                            return selected ? (
                              <div key={brandId} className="flex justify-between">
                                <p className="text-[12px] text-(--color-text-muted)">
                                  {getBrandName(brandId)}
                                </p>
                                <p className="text-[12px] text-(--color-text-muted) text-right">
                                  {selected.method_code?.toLowerCase().includes("amstorepickup")
                                    ? `${selected.name} - ${pickupStore?.name}`
                                    : selected.name}
                                </p>
                              </div>
                            ) : null;
                          })}
                        </div>
                      </>
                    ) : selectedShippingOption ? (
                      <>
                        <div className="flex justify-between">
                          <span className="text-(--color-text-secondary)">
                            Shipping
                          </span>
                          <span className="tabular-nums">
                            {formatCurrency(shippingCents)}
                          </span>
                        </div>
                        <div className="flex justify-between -mt-1">
                          <p className="text-[12px] text-(--color-text-muted)">
                            {selectedShippingOption.name}
                          </p>
                          {selectedPickupLocationId && (
                            <p className="text-[12px] text-(--color-text-muted) text-right">
                              {
                                pickupLocations.find(
                                  (location) => location.id === selectedPickupLocationId,
                                )?.name
                              }
                            </p>
                          )}
                        </div>
                      </>
                    ) : null
                  }
                </div>
              </div>
            )}

            <div className="border-t border-(--color-surface-border-light) pt-4 px-4 flex items-center justify-center gap-1.5 text-xs text-(--color-text-muted)">
              <span>Powered by</span>
              {resolvedTheme === "light" ? (
                <img
                  src="/images/nile-wordmark-dark.png"
                  alt="Nile"
                  className="h-3.5 mb-1"
                />
              ) : (
                <img
                  src="/images/nile-wordmark-light.png"
                  alt="Nile"
                  className="h-3.5 mb-1"
                />
              )}
            </div>
          </div>

          {/* Spacer for mobile sticky footer */}
          <div className="lg:hidden" style={{ height: mobileBarHeight }} />
          
        </main>
      </div>

      <footer className="hidden lg:flex border-t border-(--color-surface-border-light) p-4 z-50 items-center justify-center gap-1.5 text-xs text-(--color-text-muted)">
        <span>Powered by</span>
        {resolvedTheme === "light" ? (
          <img
            src="/images/nile-wordmark-dark.png"
            alt="Nile"
            className="h-3.5 mb-1"
          />
        ) : (
          <img
            src="/images/nile-wordmark-light.png"
            alt="Nile"
            className="h-3.5 mb-1"
          />
        )}
      </footer>


    </div>
  );

  // -------------------------------------------------------------------------
  //#endregion
  // -------------------------------------------------------------------------
}